/**
 * Curated scene presets — the *primary* visual entry point for users.
 *
 * Each scene is a complete, opinionated configuration. It explicitly sets:
 *   - Which visual LAYERS are on (particles / strings / lattice / aurora / warp / cathedral)
 *   - Bloom and post-processing restraint
 *   - Per-particle palette (colorMode)
 *   - Camera mode + framing
 *   - Atmosphere, audio reactivity, intensity
 *
 * The contract: tapping a scene results in a coherent, named "look" that's
 * been hand-tuned. Users can flip individual Layer toggles afterward to
 * add/remove elements without losing the scene's other tuning.
 */

import { BioclastParams } from "./bioclast";
import type { AltSceneType } from "../altScenes";

export type SceneControlProfile =
  | "bioclast"
  | "energyOrb"
  | "flowForm"
  | "texturedSphere"
  | "mathParticles"
  | "mesh";

/**
 * Mood buckets — high-level emotional grouping used by the UI to render
 * scenes in clusters instead of one giant flat grid. Optional on each
 * scene so legacy callers don't break.
 */
export type SceneMood =
  | "Showcase"
  | "Living Forms"
  | "Math Based"
  | "Chill"
  | "Hype"
  | "Dreamy"
  | "Trippy"
  | "Cinematic"
  | "Cosmic"
  | "Tech";

/**
 * A *delta* applied on top of the parent scene's `values`. Variants let
 * a single scene express 2–5 closely related "looks" (e.g. *Liquid Mass*
 * has Cool/Warm/Hot variants) without duplicating 30 lines of params.
 *
 * Keep variants small — only override the fields that should change.
 * The runtime always re-applies the parent first, then the variant,
 * so removing a variant cleanly reverts to the base scene.
 */
export interface SceneVariant {
  /** Short user-facing label, e.g. "Warm", "Cinema", "Pulse". */
  name: string;
  /** Optional hue override for the chip. Falls back to the parent accent. */
  accent?: number;
  /** Param overrides merged on top of the parent scene's `values`. */
  values: Partial<BioclastParams>;
}

export interface ScenePreset {
  name: string;
  subtitle: string;
  accent: number; // hue 0..360
  /** Renderer engine used by this Scene-tab recipe. Defaults to Bioclast. */
  sceneType?: "bioclast" | AltSceneType;
  /** Controls that should be offered when this scene is selected. */
  controlProfile?: SceneControlProfile;
  /** Optional mood bucket for grouping. */
  mood?: SceneMood;
  values: Partial<BioclastParams>;
  /**
   * Optional named variants. The first ("Original") is implicit and is
   * synthesized at the call site — variants here are *deltas* only.
   */
  variants?: SceneVariant[];
}

/**
 * Stable transport IDs for remote control, saved selections, and generated
 * catalogs. Display names may evolve; these IDs are the public contract.
 */
const SCENE_ID_BY_NAME: Readonly<Record<string, string>> = {
  "Liquid Mass": "liquid-mass",
  Aurora: "aurora",
  "Warp Drive": "warp-drive",
  Cathedral: "cathedral",
  "Ghost Cloud": "ghost-cloud",
  Nebula: "nebula",
  "Core Mass": "core-mass",
  "Shock Bloom": "shock-bloom",
  Storm: "storm",
  "Lava Flow": "lava-flow",
  "DNA Helix": "dna-helix",
  "Pulse Vortex": "pulse-vortex",
  "Audio Tide": "audio-tide",
  "Orbital Rings": "orbital-rings",
  "Galaxy Flythrough": "galaxy-flythrough",
  "Aurora Forest": "aurora-forest",
  "Boid Flock": "boid-flock",
  "Wind Tunnel": "wind-tunnel",
  "Mercury Pool": "mercury-pool",
  "Mountain Range": "mountain-range",
  "Fire Core": "fire-core",
  "Ion Bloom": "ion-bloom",
  "Lava Sphere": "lava-sphere",
  "Plasma Torus": "plasma-torus",
  "Drift Cube": "drift-cube",
  "Ribbon Drift": "ribbon-drift",
  "Lava Blob": "lava-blob",
  "Waveform Glow": "waveform-glow",
  "Pulse Halo": "pulse-halo",
  "Quantum Core": "quantum-core",
  "Volumetric Sphere": "volumetric-sphere",
  "Colorful Sphere": "colorful-sphere",
  "Golden Aperture": "superform",
  "Spiral Vortex": "spiral-vortex",
  "Hopf Fibration": "hopf-fibration",
  "Hopf Fibration (Linked Strands)": "hopf-fibration-linked-strands",
  "Fibonacci Sequence": "fibonacci-sequence",
  "Radial Pulse": "radial-pulse",
  "Sine Surge": "sine-surge",
  Stellarator: "stellarator",
  "Plasma Membrane": "plasma-sheet",
  "Noise Blossom": "noise-bloom",
  "Harmonic Shell": "harmonic-shell",
  "Strange Attractor": "strange-attractor",
  "Lorenz Drift": "lorenz-drift",
  "Living Shell": "organic-breath",
  "Helical Twist": "helical-twist",
  "Mycelial Bloom": "mycelial-bloom",
  "Chladni Shell": "chladni-shell",
  "Coral Skin": "coral-skin",
  "Gyroid Lattice": "gyroid-lattice",
  "Torus Knot Weave": "torus-knot-weave",
  "Cymatic Bell": "cymatic-bell",
  "Voronoi Shatter": "voronoi-shatter",
  "Klein Ribbon": "klein-ribbon",
  "Ferrofluid Crown": "ferrofluid-crown",
  "Magnetic Curtain": "magnetic-curtain",
  "Fractal Flame": "fractal-flame",
  "Vortex Cascade": "vortex-cascade",
  "Smoke Column": "smoke-column",
  Harmonograph: "harmonograph",
  "Foam Party": "foam-party",
  "Superform (GPU)": "superform-gpu",
  "Hopf Fibration (GPU)": "hopf-fibration-gpu",
  "Fibonacci Sequence (GPU)": "fibonacci-sequence-gpu",
  "Radial Pulse Field (GPU)": "radial-pulse-field-gpu",
  "Wireframe Terrain": "wireframe-terrain",
  "Wavy Form": "wavy-form",
  "Dot Wall": "dot-wall",
  "Frequency Hallway": "frequency-hallway",
  "Spiral Galaxy": "spiral-galaxy",
  "Cellular Matter": "cellular-matter",
  "Neural Field": "neural-field",
  "Infinite Petri": "infinite-petri",
  "Coherent Fold": "coherent-fold",
  "Cymatic Current": "cymatic-current",
  "Scale Lattice": "scale-lattice",
  "Deep Field": "deep-field",
  "Ramsey Flow": "ramsey-flow",
  "Sanctuary Lasers": "sanctuary-lasers",
  "Stained Glass Awakening": "stained-glass-awakening",
  "Cathedral Organ": "cathedral-organ",
  "Celestial Ascension": "celestial-ascension",
};

export const REMOTE_CANARY_SCENE_IDS = [
  "liquid-mass",
  "warp-drive",
  "cathedral",
  "dna-helix",
  "fire-core",
  "quantum-core",
  "mercury-pool",
  "pulse-halo",
] as const;

export type RemoteSceneScope = "canary" | "bioclast" | "webgl" | "gpu" | "all";
export type RemoteSceneWave = 1 | 2 | 3 | 4;
export type RemoteRuntimeTarget = "desktop-preview" | "obs-wall" | "sl-moap";

const REMOTE_FULL_SPECTRUM_SCENE_IDS = new Set([
  "audio-tide",
  "waveform-glow",
]);

const REMOTE_HEAVY_GPU_SCENE_TYPES = new Set<AltSceneType>([
  "superformGPU",
  "hopfFibrationGPU",
  "fibonacciSequenceGPU",
  "radialPulseGPU",
  "flowFormGPU",
]);

export function getRemoteSceneWave(scene: ScenePreset): RemoteSceneWave {
  const id = getSceneId(scene);
  if (REMOTE_FULL_SPECTRUM_SCENE_IDS.has(id)) return 4;
  const sceneType = scene.sceneType ?? "bioclast";
  if (sceneType === "bioclast") return 1;
  if (REMOTE_HEAVY_GPU_SCENE_TYPES.has(sceneType as AltSceneType)) return 3;
  return 2;
}

export function sceneIsAvailableInRemoteScope(
  scene: ScenePreset,
  scope: RemoteSceneScope,
): boolean {
  if (scope === "canary") {
    return (REMOTE_CANARY_SCENE_IDS as readonly string[]).includes(getSceneId(scene));
  }
  const maximumWave = scope === "bioclast" ? 1 : scope === "webgl" ? 2 : scope === "gpu" ? 3 : 4;
  return getRemoteSceneWave(scene) <= maximumWave;
}

export function parseRemoteSceneScope(value: string | null): RemoteSceneScope {
  if (value === "bioclast" || value === "webgl" || value === "gpu" || value === "all") {
    return value;
  }
  return "all";
}

export function parseRemoteRuntimeTarget(value: string | null): RemoteRuntimeTarget {
  if (value === "obs-wall" || value === "sl-moap") return value;
  return "desktop-preview";
}

export function sceneIsAvailableForRemoteTarget(
  _scene: ScenePreset,
  _target: RemoteRuntimeTarget,
): boolean {
  return true;
}

function normalizeIdentityToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function getSceneId(scene: ScenePreset): string {
  const id = SCENE_ID_BY_NAME[scene.name];
  if (!id) throw new Error(`Scene is missing a stable ID: ${scene.name}`);
  return id;
}

export function getVariantId(scene: ScenePreset, variant: SceneVariant): string {
  return `${getSceneId(scene)}:${normalizeIdentityToken(variant.name)}`;
}

export function findSceneById(id: string | null): ScenePreset | null {
  if (!id) return null;
  return SCENES.find((scene) => getSceneId(scene) === id) ?? null;
}

export function findVariantById(
  scene: ScenePreset,
  id: string | null,
): SceneVariant | null {
  if (!id) return null;
  return scene.variants?.find((variant) => getVariantId(scene, variant) === id) ?? null;
}

/**
 * Hand-curated scenes. Each one explicitly sets every layer toggle so two
 * taps in a row never produce a chimera state.
 */
export const SCENES: ScenePreset[] = [
  // ----------------------------------------------------------------
  // ROW 1 — Universal-appeal defaults
  // ----------------------------------------------------------------
  {
    name: "Liquid Mass",
    subtitle: "Cohesive cloud, gentle swirl",
    accent: 195,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 1.0,
      cameraSpin: 0.4,
      starSize: 0.22,
      intensity: 1.4,
      opacity: 0.92,
      saturation: 0.85,
      bassGravity: 1.4,
      midCurl: 1.6,
      trebleShimmer: 1.0,
      cymaticStrength: 1.6,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 0.95,
      bloomRadius: 0.55,
      bloomThreshold: 0.18,
    },
  },
  {
    name: "Aurora",
    subtitle: "Ribbon drifting overhead",
    accent: 165,
    values: {
      // Particles + aurora only — the previous scene had strings + lattice
      // on top, which made the cloud read as visual chaos.
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: true,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 1,
      conceptMode: "aurora",
      cameraMode: "orbit",
      cameraTilt: 0.35,
      cameraZoom: 0.9,
      cameraSpin: 0.3,
      starSize: 0.16,
      intensity: 1.2,
      opacity: 0.9,
      saturation: 0.85,
      bassGravity: 0.9,
      midCurl: 1.4,
      trebleShimmer: 1.6,
      cymaticStrength: 1.0,
      flowSpeed: 0.7,
      fieldTightness: 1.1,
      atmosphere: 0.7,
      bloomEnabled: true,
      bloomStrength: 1.1,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
    },
  },
  {
    name: "Warp Drive",
    subtitle: "Pure forward velocity, just the tunnel",
    accent: 215,
    values: {
      // Tunnel ONLY. Adding the particle field on top duplicates the "stars
      // rushing past" idea and reads as visual noise instead of speed.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: true,
      layerCathedral: false,
      colorMode: 2,
      conceptMode: "warp",
      cameraMode: "flythrough",
      flythroughSpeed: 0,  // static — user positions it and it stays put
      beatDolly: 0,
      cameraZoom: 1.0,
      cameraTilt: 0.5,
      cameraSpin: 0,
      starSize: 0.16,
      intensity: 1.5,
      opacity: 0.95,
      saturation: 0.8,
      bassGravity: 0.8,
      midCurl: 0.6,
      trebleShimmer: 1.4,
      cymaticStrength: 0.9,
      flowSpeed: 2.4,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.4,
      bloomRadius: 0.7,
      bloomThreshold: 0.12,
    },
  },
  {
    name: "Cathedral",
    subtitle: "Architectural pillars, orbit-able",
    accent: 175,
    values: {
      // Pillars only. Particle cloud OFF — the cathedral lines have no
      // substance, so layering them over a swirling cloud reads as visual
      // chaos.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: true,
      colorMode: 3,
      conceptMode: "cathedral",
      // Orbit camera framed slightly above and tilted down, looking at
      // the nave from the side. Slow auto-spin lets the user watch the
      // architecture rotate without doing anything; pinch / drag to
      // explore from any angle.
      cameraMode: "orbit",
      cameraTilt: 0.4,
      cameraZoom: 0.6,    // pulled back so the whole nave fits comfortably
      cameraSpin: 0,      // static — user positions it and it stays put
      starSize: 0.13,
      intensity: 1.25,
      opacity: 0.95,
      saturation: 0.7,
      bassGravity: 1.0,
      midCurl: 0.5,
      trebleShimmer: 0.6,
      cymaticStrength: 1.0,
      flowSpeed: 0.4,
      fieldTightness: 0.9,
      atmosphere: 0.45,
      backdropBlur: 30,
      bloomEnabled: true,
      bloomStrength: 1.0,
      bloomRadius: 0.55,
      bloomThreshold: 0.18,
    },
  },

  // ----------------------------------------------------------------
  // ROW 2 — Mood / quiet looks
  // ----------------------------------------------------------------
  {
    name: "Ghost Cloud",
    subtitle: "Calm, ethereal, low motion",
    accent: 274,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 0,
      conceptMode: "nebula",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.85,
      cameraSpin: 0.18,
      starSize: 0.18,
      intensity: 1.05,
      opacity: 0.78,
      saturation: 0.7,
      bassGravity: 0.6,
      midCurl: 0.5,
      trebleShimmer: 1.1,
      cymaticStrength: 0.6,
      flowSpeed: 0.22,
      fieldTightness: 1.0,
      atmosphere: 0.4,
      backdropBlur: 70,
      bloomEnabled: true,
      bloomStrength: 0.7,
      bloomRadius: 0.65,
      bloomThreshold: 0.22,
    },
  },
  {
    name: "Nebula",
    subtitle: "Wide, slow, deep-space drift",
    accent: 245,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 3,
      conceptMode: "nebula",
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 0.7,
      cameraSpin: 0.02,
      starSize: 0.2,
      intensity: 1.1,
      opacity: 0.8,
      saturation: 0.75,
      bassGravity: 0.7,
      midCurl: 0.4,
      trebleShimmer: 0.9,
      cymaticStrength: 0.7,
      flowSpeed: 0.3,
      fieldTightness: 1.05,
      atmosphere: 0.55,
      backdropBlur: 60,
      bloomEnabled: true,
      bloomStrength: 1.0,
      bloomRadius: 0.7,
      bloomThreshold: 0.2,
    },
  },

  // ----------------------------------------------------------------
  // ROW 3 — Energy / drop looks
  // ----------------------------------------------------------------
  {
    name: "Core Mass",
    subtitle: "Dense particle core, big bass",
    accent: 195,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 3,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.2,
      cameraSpin: 0.28,
      starSize: 0.28,
      intensity: 1.4,
      opacity: 0.92,
      saturation: 0.85,
      bassGravity: 2.4,
      midCurl: 1.2,
      trebleShimmer: 0.8,
      cymaticStrength: 2.6,
      flowSpeed: 0.5,
      fieldTightness: 0.74,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.1,
      bloomRadius: 0.55,
      bloomThreshold: 0.18,
    },
  },
  {
    name: "Shock Bloom",
    subtitle: "Beat-driven explosions",
    accent: 24,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 2,
      conceptMode: "bloom",
      cameraMode: "orbit",
      cameraTilt: 0.45,
      cameraZoom: 1.25,
      cameraSpin: 0.4,
      starSize: 0.24,
      intensity: 1.4,
      opacity: 0.94,
      saturation: 0.85,
      bassGravity: 2.6,
      midCurl: 1.4,
      trebleShimmer: 1.0,
      cymaticStrength: 3.2,
      flowSpeed: 0.7,
      fieldTightness: 0.85,
      atmosphere: 0.75,
      bloomEnabled: true,
      bloomStrength: 1.5,
      bloomRadius: 0.6,
      bloomThreshold: 0.1,
    },
  },
  {
    name: "Storm",
    subtitle: "Particles + strings, big amplitude",
    accent: 230,
    values: {
      // Trimmed: was lattice + aurora on top. Too many elements at once.
      layerParticles: true,
      layerStrings: true,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.0,
      cameraSpin: 0.5,
      starSize: 0.2,
      intensity: 1.3,
      opacity: 0.9,
      saturation: 0.85,
      bassGravity: 2.2,
      midCurl: 1.4,
      trebleShimmer: 1.2,
      cymaticStrength: 2.8,
      flowSpeed: 1.3,
      fieldTightness: 1.0,
      atmosphere: 0.75,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
    },
  },

  // ----------------------------------------------------------------
  // ROW 4 — Warm / experimental
  // ----------------------------------------------------------------
  {
    name: "Lava Flow",
    subtitle: "Hot orange currents, no clutter",
    accent: 18,
    values: {
      // Trimmed: was hybrid + aurora. Too busy.
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      colorMode: 2,
      conceptMode: "lava",
      cameraMode: "orbit",
      cameraTilt: 0.25,
      cameraZoom: 1.0,
      cameraSpin: 0.2,
      paletteHue: 14,
      starSize: 0.2,
      intensity: 1.35,
      opacity: 0.92,
      saturation: 0.95,
      bassGravity: 1.8,
      midCurl: 1.6,
      trebleShimmer: 0.6,
      cymaticStrength: 1.6,
      flowSpeed: 0.6,
      fieldTightness: 0.9,
      atmosphere: 0.65,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.55,
      bloomThreshold: 0.15,
    },
  },

  // ----------------------------------------------------------------
  // ROW 5 — v2 hero scenes (DNA / Vortex / Tide / Orbital)
  // ----------------------------------------------------------------
  {
    name: "DNA Helix",
    subtitle: "Spinning double helix, side view",
    accent: 290,
    values: {
      // DNA only. The helix is the entire visual.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: true,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      colorMode: 1,
      conceptMode: "none",
      // Orbit camera framed from the SIDE so the helix's twist is visible.
      // (Looking down the helix axis collapses the depth and you just see
      // a starburst of overlapping rungs.) Slight horizon tilt + slow spin
      // gives a cinematic "rotating molecule" feel.
      cameraMode: "orbit",
      cameraTilt: 0.32,   // slight horizon tilt — looking from the side
      cameraZoom: 0.8,    // pull back so the full helix fits in frame
      cameraSpin: 0.3,    // slow auto-spin for cinematic motion
      starSize: 0.22,
      intensity: 1.5,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.6,
      midCurl: 1.0,
      trebleShimmer: 1.4,
      cymaticStrength: 1.0,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.5,
      bloomEnabled: true,
      bloomStrength: 1.4,
      bloomRadius: 0.65,
      bloomThreshold: 0.12,
    },
  },
  {
    name: "Pulse Vortex",
    subtitle: "Inward spiral, kicks explode outward",
    accent: 320,
    values: {
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: true,
      layerTide: false,
      layerOrbital: false,
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.65, // looking down on the vortex from a slight angle
      cameraZoom: 0.85,
      cameraSpin: 0.15,
      paletteHue: 320,
      starSize: 0.22,
      intensity: 1.45,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 2.4,
      midCurl: 1.0,
      trebleShimmer: 1.0,
      cymaticStrength: 2.5,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.7,
      bloomEnabled: true,
      bloomStrength: 1.3,
      bloomRadius: 0.6,
      bloomThreshold: 0.13,
    },
  },
  {
    name: "Audio Tide",
    subtitle: "Live spectrum as terrain you fly over",
    accent: 200,
    controlProfile: "mesh",
    values: {
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: true,
      layerOrbital: false,
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.78, // high tilt — looking down at the terrain
      cameraZoom: 0.55, // pulled back so the whole spectrum is in view
      cameraSpin: 0.0,
      starSize: 0.18,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.85,
      bassGravity: 1.0,
      midCurl: 1.0,
      trebleShimmer: 1.2,
      cymaticStrength: 0.8,
      flowSpeed: 0.6,
      fieldTightness: 1.0,
      atmosphere: 0.5,
      bloomEnabled: true,
      bloomStrength: 1.0,
      bloomRadius: 0.55,
      bloomThreshold: 0.18,
    },
  },
  {
    name: "Orbital Rings",
    subtitle: "Concentric bands, cinematic god view",
    accent: 50,
    values: {
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: true,
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.45,
      cameraZoom: 0.65,
      cameraSpin: 0.18,
      starSize: 0.22,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.88,
      bassGravity: 1.6,
      midCurl: 1.2,
      trebleShimmer: 1.4,
      cymaticStrength: 1.2,
      flowSpeed: 0.8,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
    },
  },

  // ----------------------------------------------------------------
  // ROW 6 — v3 cosmic / immersive scenes
  // ----------------------------------------------------------------
  {
    name: "Galaxy Flythrough",
    subtitle: "Spiral arms + warp stars",
    accent: 220,
    values: {
      // Galaxy disc + world-space warp streaks. Particle cloud OFF —
      // the disc IS the cloud, and the streaks supply foreground motion.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: true,
      layerAuroraForest: false,
      colorMode: 1,
      conceptMode: "none",
      // Slight tilt-up looking into one spiral arm, cinematic slow drift.
      cameraMode: "orbit",
      cameraTilt: 0.35,
      cameraZoom: 0.75,
      cameraSpin: 0.12,
      paletteHue: 200,
      starSize: 0.18,
      intensity: 1.45,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.4,
      midCurl: 1.0,
      trebleShimmer: 1.2,
      cymaticStrength: 1.0,
      flowSpeed: 1.2,
      fieldTightness: 1.0,
      atmosphere: 0.5,
      bloomEnabled: true,
      bloomStrength: 1.4,
      bloomRadius: 0.7,
      bloomThreshold: 0.12,
    },
  },
  {
    name: "Aurora Forest",
    subtitle: "Stand inside a forest of dancing ribbons",
    accent: 145,
    values: {
      // Vertical aurora ribbons standing in a circle around the camera.
      // Pure ribbons — no particle cloud, no other layers, so the user
      // experiences being IN the forest looking out at the curtains.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: true,
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.42, // slight horizon view
      cameraZoom: 0.55, // pulled back so multiple ribbons are visible
      cameraSpin: 0.1,  // very slow auto-spin to invite exploration
      paletteHue: 145,
      starSize: 0.18,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.95,
      bassGravity: 1.0,
      midCurl: 1.4,
      trebleShimmer: 1.6,
      cymaticStrength: 0.8,
      flowSpeed: 0.8,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.3,
      bloomRadius: 0.65,
      bloomThreshold: 0.13,
    },
  },

  // ----------------------------------------------------------------
  // ROW 7 — v4 organic / immersive scenes
  // ----------------------------------------------------------------
  {
    name: "Boid Flock",
    subtitle: "Glowing swarm reacts to every kick",
    accent: 100,
    values: {
      // Boid swarm only. Self-organizes into a coherent flock; bass kicks
      // tighten it into a defensive ball, treble fans them apart.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: true,
      layerWind: false,
      layerMercury: false,
      colorMode: 2, // velocity — boids hue-shift by their own speed
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.7,
      cameraSpin: 0.18, // slow auto-orbit so the swarm reads from all sides
      starSize: 0.2,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.85,
      bassGravity: 1.4,
      midCurl: 1.0,
      trebleShimmer: 1.2,
      cymaticStrength: 1.0,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
    },
  },
  {
    name: "Wind Tunnel",
    subtitle: "Organic flow, beats reverse direction",
    accent: 30,
    values: {
      // Camera-locked wind tunnel. Particles flow toward the lens; a
      // strong beat momentarily reverses the direction so the kick
      // visibly "blows the wind backward."
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: true,
      layerMercury: false,
      colorMode: 2,
      conceptMode: "none",
      // Static framing — the tunnel motion IS the visual; camera should
      // hold still so the flow direction reads clearly.
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.0,
      cameraSpin: 0,
      paletteHue: 28,
      starSize: 0.2,
      intensity: 1.45,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.4,
      midCurl: 1.6,   // more curl = more streamline weave
      trebleShimmer: 1.3,
      cymaticStrength: 1.0,
      flowSpeed: 1.4,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.3,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
    },
  },
  {
    name: "Mercury Pool",
    subtitle: "Reflective liquid waves to the music",
    accent: 195,
    controlProfile: "mesh",
    values: {
      // Wide reflective surface in front of the camera. Treble drops
      // ripples into the pool; bass heaves the whole surface.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: true,
      colorMode: 0, // ice — cool blue/silver glacier tint suits liquid metal
      conceptMode: "none",
      // Glancing-angle camera so the surface reads as horizontal liquid.
      cameraMode: "orbit",
      cameraTilt: 0.18, // very low tilt — looking across the pool
      cameraZoom: 0.65,
      cameraSpin: 0.12, // gentle drift around the pool
      paletteHue: 200,
      starSize: 0.2,
      intensity: 1.3,
      opacity: 0.95,
      saturation: 0.7,
      bassGravity: 1.4,
      midCurl: 1.0,
      trebleShimmer: 1.4,
      cymaticStrength: 1.2,
      flowSpeed: 0.8,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 0.55,
      bloomRadius: 0.45,
      bloomThreshold: 0.42,
    },
  },
  {
    name: "Mountain Range",
    subtitle: "Fly through endless terrain",
    accent: 35,
    controlProfile: "mesh",
    values: {
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: true,
      colorMode: 1,
      conceptMode: "none",
      // Camera floats above the terrain at a gentle downward tilt;
      // flowSpeed in the params drives forward flight through the range.
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.55,
      cameraSpin: 0,
      paletteHue: 30,
      starSize: 0.18,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.85,
      bassGravity: 1.6,
      midCurl: 1.0,
      trebleShimmer: 1.0,
      cymaticStrength: 1.0,
      flowSpeed: 1.2,    // forward flight speed; 0 to freeze the range
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
    },
  },

  // ----------------------------------------------------------------
  // ROW 8 — Emergence physics scenes (lava-lamp / blob morphing)
  // ----------------------------------------------------------------
  // These scenes use the new self-organizing physics: cohesion to a
  // target shape + noise + bass repulsion. Reads as a fluid blob
  // rather than orbital motion. All built on the particle layer with
  // particleMode set to "emergence".
  {
    name: "Fire Core",
    subtitle: "Volumetric flame inside a glassy sphere",
    accent: 18,
    controlProfile: "energyOrb",
    mood: "Cinematic",
    variants: [
      {
        name: "Solar",
        accent: 42,
        values: {
          energyOrbPrimaryColor: "#fff2a0",
          energyOrbSecondaryColor: "#ff5a00",
          energyOrbDensity: 1.55,
          energyOrbAtmosphereGlow: 0.18,
          bloomStrength: 0.62,
        },
      },
      {
        name: "Inferno",
        accent: 5,
        values: {
          energyOrbPrimaryColor: "#ffd36a",
          energyOrbSecondaryColor: "#ff1100",
          energyOrbDensity: 1.75,
          energyOrbFractalDecay: -13.4,
          energyOrbAtmosphereGlow: 0.24,
          fxRgbShift: 0.026,
        },
      },
    ],
    values: {
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      layerEnergyOrb: true,
      layerWaveform: false,
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.44,
      cameraZoom: 0.86,
      cameraSpin: 0.18,
      paletteHue: 18,
      starSize: 0.18,
      intensity: 0.88,
      opacity: 0.95,
      saturation: 0.95,
      bassGravity: 1.7,
      midCurl: 1.25,
      trebleShimmer: 1.2,
      cymaticStrength: 1.1,
      flowSpeed: 0.9,
      fieldTightness: 1.0,
      atmosphere: 0.38,
      bloomEnabled: true,
      bloomStrength: 0.52,
      bloomRadius: 0.42,
      bloomThreshold: 0.4,
      postFxEnabled: true,
      fxVignette: 0.18,
      fxRgbShift: 0.014,
      fxFilmGrain: 0.02,
      energyOrbSize: 0.24,
      energyOrbPrimaryColor: "#ffd36a",
      energyOrbSecondaryColor: "#ff2a00",
      energyOrbDensity: 1.02,
      energyOrbSpeed: 0.58,
      energyOrbRotation: 0.46,
      energyOrbFractalIters: 4,
      energyOrbFractalScale: 0.75,
      energyOrbFractalDecay: -14.0,
      energyOrbSmoothness: 0.012,
      energyOrbAsymmetry: 0.45,
      energyOrbAtmosphereGlow: 0.08,
      energyOrbAtmosphereLevel: 0.7,
      energyOrbAtmosphereScale: 1.04,
    },
  },
  {
    name: "Ion Bloom",
    subtitle: "Cyan-violet volumetric light bloom",
    accent: 188,
    controlProfile: "energyOrb",
    mood: "Cosmic",
    variants: [
      {
        name: "Cyan",
        accent: 182,
        values: {
          energyOrbPrimaryColor: "#00ffee",
          energyOrbSecondaryColor: "#31a6ff",
          energyOrbDensity: 0.9,
          energyOrbAtmosphereGlow: 0.14,
        },
      },
      {
        name: "Violet",
        accent: 280,
        values: {
          energyOrbPrimaryColor: "#b9f7ff",
          energyOrbSecondaryColor: "#8a2bff",
          energyOrbDensity: 1.0,
          energyOrbFractalDecay: -15.4,
          fxRgbShift: 0.026,
        },
      },
    ],
    values: {
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      layerEnergyOrb: true,
      layerWaveform: false,
      colorMode: 3,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.84,
      cameraSpin: 0.12,
      paletteHue: 190,
      starSize: 0.14,
      intensity: 0.82,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.1,
      midCurl: 1.35,
      trebleShimmer: 1.7,
      cymaticStrength: 1.1,
      flowSpeed: 0.65,
      fieldTightness: 1.0,
      atmosphere: 0.42,
      bloomEnabled: true,
      bloomStrength: 0.48,
      bloomRadius: 0.4,
      bloomThreshold: 0.44,
      postFxEnabled: true,
      fxVignette: 0.1,
      fxRgbShift: 0.014,
      fxFilmGrain: 0.012,
      energyOrbSize: 0.23,
      energyOrbPrimaryColor: "#00ffee",
      energyOrbSecondaryColor: "#8a2bff",
      energyOrbDensity: 0.68,
      energyOrbSpeed: 0.42,
      energyOrbRotation: 0.28,
      energyOrbFractalIters: 3,
      energyOrbFractalScale: 0.75,
      energyOrbFractalDecay: -16.7,
      energyOrbSmoothness: 0.05,
      energyOrbAsymmetry: 0.45,
      energyOrbAtmosphereGlow: 0.07,
      energyOrbAtmosphereLevel: 0.64,
      energyOrbAtmosphereScale: 1.045,
    },
  },
  {
    name: "Lava Sphere",
    subtitle: "Breathing blob, kicks blow it open",
    accent: 25,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      particleMode: "emergence",
      emergenceShape: "sphere",
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.85,
      cameraSpin: 0.2,
      paletteHue: 22,
      starSize: 0.22,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.8,
      midCurl: 1.4,
      trebleShimmer: 1.2,
      cymaticStrength: 1.6,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.4,
      bloomRadius: 0.65,
      bloomThreshold: 0.13,
    },
  },
  {
    name: "Plasma Torus",
    subtitle: "Spinning donut deforms with bass",
    accent: 280,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      particleMode: "emergence",
      emergenceShape: "torus",
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 0.7,
      cameraSpin: 0.18,
      paletteHue: 280,
      starSize: 0.2,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.5,
      midCurl: 1.6,
      trebleShimmer: 1.3,
      cymaticStrength: 1.4,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.3,
      bloomRadius: 0.6,
      bloomThreshold: 0.14,
    },
  },
  {
    name: "Drift Cube",
    subtitle: "Geometric mass with melting edges",
    accent: 175,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      particleMode: "emergence",
      emergenceShape: "cube",
      colorMode: 3,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.42,
      cameraZoom: 0.6,
      cameraSpin: 0.22,
      paletteHue: 175,
      starSize: 0.18,
      intensity: 1.35,
      opacity: 0.95,
      saturation: 0.85,
      bassGravity: 1.6,
      midCurl: 1.0,
      trebleShimmer: 0.9,
      cymaticStrength: 1.2,
      flowSpeed: 0.7,
      fieldTightness: 1.0,
      atmosphere: 0.5,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.55,
      bloomThreshold: 0.16,
    },
  },
  {
    name: "Ribbon Drift",
    subtitle: "Long curving form, swept by mids",
    accent: 145,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      particleMode: "emergence",
      emergenceShape: "ribbon",
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.65,
      cameraSpin: 0.16,
      paletteHue: 145,
      starSize: 0.2,
      intensity: 1.35,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.2,
      midCurl: 1.6,
      trebleShimmer: 1.3,
      cymaticStrength: 1.0,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.6,
      bloomThreshold: 0.15,
    },
  },
  {
    name: "Lava Blob",
    subtitle: "Pure organic flow, no shape lock",
    accent: 320,
    values: {
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      particleMode: "emergence",
      emergenceShape: "blob",
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.7,
      cameraSpin: 0.2,
      paletteHue: 320,
      starSize: 0.22,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 2.2,
      midCurl: 1.4,
      trebleShimmer: 1.0,
      cymaticStrength: 1.8,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.4,
      bloomRadius: 0.65,
      bloomThreshold: 0.13,
    },
  },

  // ----------------------------------------------------------------
  // ROW 9 — Hybrid scenes that showcase the waveform overlay
  // ----------------------------------------------------------------
  {
    name: "Waveform Glow",
    subtitle: "Particle field + dancing audio waveform",
    accent: 195,
    values: {
      // Liquid Mass-style cloud underneath, with the waveform overlay
      // on top to show the actual audio shape. Together they read as
      // "the music made visible" in the most literal sense.
      layerParticles: true,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: false,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      layerWaveform: true,
      waveformStyle: "line",
      colorMode: 1,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 1.0,
      cameraSpin: 0.4,
      starSize: 0.22,
      intensity: 1.4,
      opacity: 0.9,
      saturation: 0.85,
      bassGravity: 1.4,
      midCurl: 1.6,
      trebleShimmer: 1.2,
      cymaticStrength: 1.6,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.1,
      bloomRadius: 0.6,
      bloomThreshold: 0.16,
    },
  },
  {
    name: "Pulse Halo",
    subtitle: "Vortex with circular waveform halo",
    accent: 320,
    values: {
      // Pulse Vortex with the radial waveform sitting like a halo on top.
      // The vortex's bass-driven breath + the waveform's circular pulse
      // sync into a single hypnotic rhythm.
      layerParticles: false,
      layerStrings: false,
      layerLattice: false,
      layerAurora: false,
      layerWarp: false,
      layerCathedral: false,
      layerDnaHelix: false,
      layerVortex: true,
      layerTide: false,
      layerOrbital: false,
      layerGalaxy: false,
      layerAuroraForest: false,
      layerBoids: false,
      layerWind: false,
      layerMercury: false,
      layerMountains: false,
      layerWaveform: true,
      waveformStyle: "radial",
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.65,
      cameraZoom: 0.85,
      cameraSpin: 0.15,
      starSize: 0.22,
      intensity: 1.45,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 2.4,
      midCurl: 1.0,
      trebleShimmer: 1.0,
      cymaticStrength: 2.5,
      flowSpeed: 1.0,
      fieldTightness: 1.0,
      atmosphere: 0.7,
      bloomEnabled: true,
      bloomStrength: 1.3,
      bloomRadius: 0.6,
      bloomThreshold: 0.13,
    },
  },
  {
    name: "Quantum Core",
    subtitle: "Volumetric core, cool reactor glow",
    accent: 222,
    controlProfile: "energyOrb",
    mood: "Cosmic",
    values: {
      layerEnergyOrb: true,
      colorMode: 2,
      conceptMode: "none",
      cameraMode: "orbit",
      cameraTilt: 0.58,
      cameraZoom: 0.82,
      cameraSpin: 0.18,
      starSize: 0.16,
      intensity: 0.82,
      opacity: 0.94,
      saturation: 0.9,
      bassGravity: 1.2,
      midCurl: 1.4,
      trebleShimmer: 1.7,
      cymaticStrength: 1.4,
      flowSpeed: 0.9,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 0.42,
      bloomRadius: 0.38,
      bloomThreshold: 0.5,
      energyOrbSize: 0.2,
      energyOrbPrimaryColor: "#74f7ff",
      energyOrbSecondaryColor: "#7846ff",
      energyOrbDensity: 0.9,
      energyOrbSpeed: 0.72,
      energyOrbRotation: 0.55,
      energyOrbFractalIters: 5,
      energyOrbFractalScale: 0.86,
      energyOrbFractalDecay: -13.8,
      energyOrbSmoothness: 0.015,
      energyOrbAsymmetry: 0.34,
      energyOrbAtmosphereGlow: 0.08,
      energyOrbAtmosphereLevel: 0.58,
      energyOrbAtmosphereScale: 1.05,
    },
  },
  {
    name: "Volumetric Sphere",
    subtitle: "Hue-shift audio sphere with folded surface light",
    accent: 24,
    sceneType: "texturedSphere",
    controlProfile: "texturedSphere",
    mood: "Trippy",
    variants: [
      {
        name: "Amber",
        accent: 24,
        values: {
          paletteHue: 24,
          sphereBaseColor: "#ff7c2c",
          sphereAccentColor: "#ffd36a",
          sphereNoiseSpeed: 1.1,
          sphereColorIntensity: 2.15,
        },
      },
      {
        name: "Ultraviolet",
        accent: 276,
        values: {
          paletteHue: 276,
          sphereBaseColor: "#8a2bff",
          sphereAccentColor: "#00ffee",
          sphereNoiseSpeed: 0.92,
          sphereColorIntensity: 2.35,
        },
      },
    ],
    values: {
      paletteHue: 24,
      colorMode: 2,
      cameraMode: "orbit",
      cameraTilt: 0.52,
      cameraZoom: 0.86,
      cameraSpin: 0.3,
      starSize: 0.18,
      intensity: 0.9,
      opacity: 0.96,
      saturation: 0.96,
      bassGravity: 1.35,
      midCurl: 1.35,
      trebleShimmer: 1.65,
      cymaticStrength: 1.35,
      flowSpeed: 1.6,
      atmosphere: 0.62,
      bloomEnabled: true,
      bloomStrength: 0.5,
      bloomRadius: 0.42,
      bloomThreshold: 0.42,
      sphereDetail: 144,
      sphereSize: 1.6,
      sphereDisplacement: 0.5,
      sphereVertexAudioMix: 0.9,
      sphereNoiseScale: 2.8,
      sphereNoiseIntensity: 0.72,
      sphereNoiseSpeed: 1.05,
      sphereWaveFrequency: 1.05,
      sphereWaveSpeed: 1.35,
      sphereColorIntensity: 1.35,
      sphereMotionSmoothing: 0.68,
      sphereRotationSpeed: 0.32,
      sphereBaseColor: "#ff7c2c",
      sphereAccentColor: "#ffd36a",
      sphereSolidInterior: false,
    },
  },
  {
    name: "Colorful Sphere",
    subtitle: "Audio-reactive surface, rainbow tension",
    accent: 292,
    sceneType: "texturedSphere",
    controlProfile: "texturedSphere",
    mood: "Trippy",
    values: {
      paletteHue: 292,
      colorMode: 1,
      cameraMode: "orbit",
      cameraTilt: 0.48,
      cameraZoom: 0.9,
      cameraSpin: 0.28,
      starSize: 0.18,
      intensity: 0.95,
      opacity: 0.96,
      saturation: 0.98,
      bassGravity: 1.2,
      midCurl: 1.45,
      trebleShimmer: 1.4,
      flowSpeed: 0.9,
      atmosphere: 0.52,
      bloomEnabled: true,
      bloomStrength: 0.48,
      bloomRadius: 0.4,
      bloomThreshold: 0.44,
      sphereDetail: 160,
      sphereSize: 1.65,
      sphereDisplacement: 0.42,
      sphereVertexAudioMix: 1.1,
      sphereNoiseScale: 3.2,
      sphereNoiseIntensity: 0.65,
      sphereNoiseSpeed: 0.85,
      sphereWaveFrequency: 1.2,
      sphereWaveSpeed: 1.05,
      sphereColorIntensity: 1.45,
      sphereMotionSmoothing: 0.72,
      sphereRotationSpeed: 0.5,
      sphereBaseColor: "",
      sphereAccentColor: "",
      sphereSolidInterior: false,
    },
  },
  {
    name: "Golden Aperture",
    subtitle: "Fibonacci sphere opening through a Gielis aperture",
    accent: 28,
    sceneType: "superform",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 28,
      colorMode: 1,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.72,
      cameraSpin: 0.04,
      starSize: 0.2,
      intensity: 1.42,
      opacity: 0.94,
      saturation: 0.82,
      bassGravity: 1.05,
      midCurl: 1.1,
      trebleShimmer: 1.1,
      cymaticStrength: 1.6,
      flowSpeed: 1.5,
      fieldTightness: 1.0,
      atmosphere: 0.78,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.64,
      bloomThreshold: 0.14,
      mathParticleCount: 52_000,
      mathPointSize: 0.62,
      mathAmplitude: 0.56,
      mathFrequency: 1,
      mathRotationSpeed: 0.34,
      mathDepthScale: 0.68,
      mathWarmth: 0.96,
    },
    variants: [
      { name: "Sphere", accent: 36, values: { mathAmplitude: 0.36, mathFrequency: 1, cameraSpin: 0.04 } },
      { name: "Petals", accent: 312, values: { mathFrequency: 3, mathAmplitude: 0.78 } },
      { name: "Dense", accent: 48, values: { mathParticleCount: 70_000, mathPointSize: 0.58 } },
    ],
  },
  {
    name: "Spiral Vortex",
    subtitle: "Nested torus flow with gold inner ring",
    accent: 196,
    sceneType: "spiralVortex",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 196,
      colorMode: 2,
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 0.95,
      cameraSpin: 0.04,
      starSize: 0.2,
      intensity: 1.3,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.1,
      midCurl: 1.15,
      trebleShimmer: 1.05,
      cymaticStrength: 1.6,
      flowSpeed: 1.6,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 0.82,
      bloomRadius: 0.52,
      bloomThreshold: 0.38,
      mathParticleCount: 56_000,
      mathPointSize: 0.7,
      mathAmplitude: 0.62,
      mathFrequency: 1,
      mathRotationSpeed: 0.36,
      mathDepthScale: 0.72,
      mathWarmth: 0.42,
    },
    variants: [
      { name: "Drain", accent: 42, values: { mathFrequency: 2.4, mathAmplitude: 0.82, bassGravity: 1.3 } },
      { name: "Calm", accent: 190, values: { mathRotationSpeed: 0.16, flowSpeed: 0.9 } },
      { name: "Wide", accent: 32, values: { cameraZoom: 0.82, mathParticleCount: 76_000, mathPointSize: 0.52 } },
    ],
  },
  {
    name: "Hopf Fibration",
    subtitle: "S3 stereographic projection - linked Hopf fiber rings",
    accent: 38,
    sceneType: "hopfFibration",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 38,
      colorMode: 3,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.0,
      cameraSpin: 0.04,
      starSize: 0.18,
      intensity: 1.18,
      opacity: 0.94,
      saturation: 0.84,
      bassGravity: 1.0,
      midCurl: 1.05,
      trebleShimmer: 1.05,
      cymaticStrength: 1.2,
      flowSpeed: 1.4,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 0.46,
      bloomRadius: 0.42,
      bloomThreshold: 0.54,
      mathParticleCount: 52_000,
      mathPointSize: 0.42,
      mathAmplitude: 0.42,
      mathFrequency: 1,
      mathRotationSpeed: 0.34,
      mathDepthScale: 0.74,
      mathWarmth: 0.78,
    },
    variants: [
      { name: "Cool", accent: 205, values: { mathWarmth: 0.22, paletteHue: 205 } },
      { name: "Topology", accent: 38, values: { mathParticleCount: 62_000, mathPointSize: 0.36, bloomStrength: 0.4 } },
      { name: "Halo", accent: 52, values: { mathAmplitude: 0.58, bloomStrength: 0.52, bloomThreshold: 0.52 } },
    ],
  },
  {
    name: "Hopf Fibration (Linked Strands)",
    subtitle: "Park-parameterized fibers, continuous SO(4) re-fibration",
    accent: 268,
    sceneType: "hopfTubes",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 268,
      colorMode: 3,
      cameraMode: "orbit",
      cameraTilt: 0.52,
      cameraZoom: 0.82,
      cameraSpin: 0.22,
      starSize: 0.22,
      intensity: 1.4,
      opacity: 0.94,
      saturation: 0.9,
      bassGravity: 1.1,
      midCurl: 1.55,
      trebleShimmer: 1.6,
      cymaticStrength: 1.85,
      flowSpeed: 0.9,
      fieldTightness: 1.0,
      atmosphere: 0.64,
      bloomEnabled: true,
      bloomStrength: 1.15,
      bloomRadius: 0.66,
      bloomThreshold: 0.15,
      feedbackStrength: 0.03,
      feedbackDecay: 0.968,
    },
  },
  {
    name: "Fibonacci Sequence",
    subtitle: "Golden-angle phyllotaxis spiral with nautilus growth flow",
    accent: 45,
    sceneType: "fibonacciSequence",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 42,
      colorMode: 1,
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 0.95,
      cameraSpin: 0.045,
      starSize: 0.18,
      intensity: 1.18,
      opacity: 0.95,
      saturation: 0.9,
      bassGravity: 1.05,
      midCurl: 1.05,
      trebleShimmer: 1.1,
      cymaticStrength: 1.2,
      flowSpeed: 1.55,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 0.48,
      bloomRadius: 0.46,
      bloomThreshold: 0.5,
      mathParticleCount: 48_000,
      mathPointSize: 0.46,
      mathAmplitude: 0.5,
      mathFrequency: 1,
      mathRotationSpeed: 0.32,
      mathDepthScale: 0.82,
      mathWarmth: 0.86,
    },
    variants: [
      { name: "Sunflower", accent: 48, values: { mathDepthScale: 0.38, mathAmplitude: 0.34, cameraZoom: 1.06 } },
      { name: "Nautilus", accent: 34, values: { mathDepthScale: 1, mathAmplitude: 0.62, flowSpeed: 1.3 } },
      { name: "Dense", accent: 52, values: { mathParticleCount: 68_000, mathPointSize: 0.36, bloomStrength: 0.42 } },
    ],
  },

  // ----------------------------------------------------------------
  // Math Based — remaining Immersiom formations (GPGPU engine)
  // Each routes to a SuperformGPUScene with a distinct formation ID.
  // ----------------------------------------------------------------
  {
    name: "Radial Pulse",
    subtitle: "Fibonacci sphere wineglass with concentric ring waves",
    accent: 42,
    sceneType: "radialPulse",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 35, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 1.0, cameraSpin: 0.04,
      starSize: 0.2, intensity: 1.45, opacity: 0.95, saturation: 0.92,
      bassGravity: 1.15, midCurl: 1.05, trebleShimmer: 1.0,
      cymaticStrength: 1.4, flowSpeed: 1.55, fieldTightness: 1.0,
      atmosphere: 0.6, bloomEnabled: true, bloomStrength: 0.86,
      bloomRadius: 0.54, bloomThreshold: 0.36,
      mathParticleCount: 52_000,
      mathPointSize: 0.78,
      mathAmplitude: 0.58,
      mathFrequency: 1,
      mathRotationSpeed: 0.32,
      mathDepthScale: 0.68,
      mathWarmth: 0.84,
    },
    variants: [
      { name: "Float", accent: 42, values: { cameraSpin: 0.03, mathRotationSpeed: 0.16, flowSpeed: 0.9 } },
      { name: "Cool", accent: 205, values: { mathWarmth: 0.28, paletteHue: 205 } },
      { name: "Rings", accent: 50, values: { mathFrequency: 2.4, mathAmplitude: 0.72, bloomStrength: 0.85 } },
    ],
  },
  {
    name: "Sine Surge",
    subtitle: "Layered sine waves rolling along the sphere",
    accent: 162,
    sceneType: "sineSurge",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 162, colorMode: 3, cameraMode: "orbit",
      cameraTilt: 0.55, cameraZoom: 0.88, cameraSpin: 0.22,
      starSize: 0.21, intensity: 1.4, opacity: 0.93, saturation: 0.88,
      bassGravity: 1.2, midCurl: 1.7, trebleShimmer: 1.35,
      cymaticStrength: 2.0, flowSpeed: 1.05, fieldTightness: 1.0,
      atmosphere: 0.62, bloomEnabled: true, bloomStrength: 1.15,
      bloomRadius: 0.6, bloomThreshold: 0.15,
      feedbackStrength: 0.035, feedbackDecay: 0.965,
    },
  },
  {
    name: "Stellarator",
    subtitle: "Helical flux ribbons twist around a sphere",
    accent: 290,
    sceneType: "stellarator",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 290, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.6, cameraZoom: 0.82, cameraSpin: 0.28,
      starSize: 0.22, intensity: 1.5, opacity: 0.94, saturation: 0.9,
      bassGravity: 1.25, midCurl: 1.65, trebleShimmer: 1.45,
      cymaticStrength: 2.1, flowSpeed: 1.0, fieldTightness: 1.0,
      atmosphere: 0.66, bloomEnabled: true, bloomStrength: 1.25,
      bloomRadius: 0.64, bloomThreshold: 0.14,
      feedbackStrength: 0.04, feedbackDecay: 0.962,
    },
  },
  {
    name: "Plasma Membrane",
    subtitle: "Layered audio waves traveling across a living membrane",
    accent: 24,
    sceneType: "plasmaSheet",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 24, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.42, cameraZoom: 0.96, cameraSpin: 0.045,
      starSize: 0.16, intensity: 1.35, opacity: 0.92, saturation: 0.82,
      bassGravity: 1.15, midCurl: 1.45, trebleShimmer: 1.35,
      cymaticStrength: 1.45, flowSpeed: 0.92, fieldTightness: 1,
      atmosphere: 0.78, bloomEnabled: true, bloomStrength: 1.15,
      bloomRadius: 0.64, bloomThreshold: 0.14,
      feedbackStrength: 0, feedbackDecay: 0.97,
      mathParticleCount: 64_000,
      mathPointSize: 0.56,
      mathAmplitude: 1.15,
      mathFrequency: 1.25,
      mathRotationSpeed: 0.14,
      mathDepthScale: 0.72,
      mathWarmth: 0.95,
    },
    variants: [
      { name: "Glacial", accent: 196, values: { paletteHue: 196, mathWarmth: 0.12, flowSpeed: 0.7 } },
      { name: "Solar", accent: 34, values: { paletteHue: 34, mathWarmth: 0.82, saturation: 0.72 } },
      { name: "Onset", accent: 186, values: { bassGravity: 1.4, mathAmplitude: 1.4, bloomStrength: 0.56 } },
    ],
  },
  {
    name: "Noise Blossom",
    subtitle: "Layered sine-noise blossoming along a coherent shell",
    accent: 26,
    sceneType: "noiseBloom",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 26, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 1, cameraSpin: 0.04,
      starSize: 0.17, intensity: 1.4, opacity: 0.93, saturation: 0.8,
      bassGravity: 1.12, midCurl: 1.65, trebleShimmer: 1.45,
      cymaticStrength: 1.55, flowSpeed: 0.78, fieldTightness: 1,
      atmosphere: 0.76, bloomEnabled: true, bloomStrength: 1.2,
      bloomRadius: 0.66, bloomThreshold: 0.13,
      feedbackStrength: 0, feedbackDecay: 0.97,
      mathParticleCount: 62_000,
      mathPointSize: 0.56,
      mathAmplitude: 0.92,
      mathFrequency: 1.4,
      mathRotationSpeed: 0.18,
      mathDepthScale: 0.68,
      mathWarmth: 0.96,
    },
    variants: [
      { name: "Iris", accent: 312, values: { paletteHue: 312, mathWarmth: 0.52, mathFrequency: 2.2 } },
      { name: "Pearl", accent: 48, values: { saturation: 0.32, mathWarmth: 0.7, bloomStrength: 0.4 } },
      { name: "Wild", accent: 76, values: { mathAmplitude: 1.35, midCurl: 2, trebleShimmer: 1.8 } },
    ],
  },
  {
    name: "Harmonic Shell",
    subtitle: "Integer azimuthal modes ripple across the sphere",
    accent: 220,
    sceneType: "harmonicShell",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 220, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.52, cameraZoom: 0.86, cameraSpin: 0.24,
      starSize: 0.21, intensity: 1.4, opacity: 0.93, saturation: 0.88,
      bassGravity: 1.1, midCurl: 1.55, trebleShimmer: 1.75,
      cymaticStrength: 2.0, flowSpeed: 0.92, fieldTightness: 1.0,
      atmosphere: 0.64, bloomEnabled: true, bloomStrength: 1.15,
      bloomRadius: 0.62, bloomThreshold: 0.15,
      feedbackStrength: 0.028, feedbackDecay: 0.968,
    },
  },
  {
    name: "Strange Attractor",
    subtitle: "Chaotic attractor curve sampled by particles",
    accent: 308,
    sceneType: "strangeAttractor",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 308, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.62, cameraZoom: 0.78, cameraSpin: 0.3,
      starSize: 0.2, intensity: 1.45, opacity: 0.92, saturation: 0.87,
      bassGravity: 1.0, midCurl: 1.5, trebleShimmer: 1.65,
      cymaticStrength: 1.7, flowSpeed: 1.05, fieldTightness: 1.05,
      atmosphere: 0.58, bloomEnabled: true, bloomStrength: 1.1,
      bloomRadius: 0.6, bloomThreshold: 0.16,
      feedbackStrength: 0.025, feedbackDecay: 0.97,
    },
  },
  {
    name: "Lorenz Drift",
    subtitle: "Lorenz lobes drifting through the origin",
    accent: 188,
    sceneType: "lorenzDrift",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 188, colorMode: 3, cameraMode: "orbit",
      cameraTilt: 0.55, cameraZoom: 0.84, cameraSpin: 0.26,
      starSize: 0.2, intensity: 1.42, opacity: 0.92, saturation: 0.88,
      bassGravity: 1.2, midCurl: 1.45, trebleShimmer: 1.45,
      cymaticStrength: 1.75, flowSpeed: 1.0, fieldTightness: 1.0,
      atmosphere: 0.62, bloomEnabled: true, bloomStrength: 1.12,
      bloomRadius: 0.6, bloomThreshold: 0.15,
      feedbackStrength: 0.03, feedbackDecay: 0.967,
    },
  },
  {
    name: "Living Shell",
    subtitle: "A living shell with asymmetric inhale and heartbeat lobes",
    accent: 18,
    sceneType: "organicBreath",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 18, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 1.04, cameraSpin: 0.035,
      starSize: 0.18, intensity: 1.34, opacity: 0.93, saturation: 0.78,
      bassGravity: 1.25, midCurl: 1.22, trebleShimmer: 1.1,
      cymaticStrength: 1.35, flowSpeed: 0.55, fieldTightness: 1,
      atmosphere: 0.76, bloomEnabled: true, bloomStrength: 1.12,
      bloomRadius: 0.64, bloomThreshold: 0.15,
      feedbackStrength: 0, feedbackDecay: 0.972,
      mathParticleCount: 58_000,
      mathPointSize: 0.6,
      mathAmplitude: 0.7,
      mathFrequency: 1,
      mathRotationSpeed: 0.12,
      mathDepthScale: 0.72,
      mathWarmth: 0.96,
    },
    variants: [
      { name: "Deep Breath", accent: 154, values: { flowSpeed: 0.34, mathAmplitude: 1.05, bassGravity: 1.5 } },
      { name: "Pearl", accent: 190, values: { paletteHue: 190, saturation: 0.34, mathWarmth: 0.18 } },
      { name: "Heartbeat", accent: 346, values: { paletteHue: 346, bassGravity: 1.7, bloomStrength: 0.5 } },
    ],
  },
  {
    name: "Helical Twist",
    subtitle: "Double-helix twist with radial pulse on the sphere",
    accent: 248,
    sceneType: "helicalTwist",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 248, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.58, cameraZoom: 0.82, cameraSpin: 0.28,
      starSize: 0.21, intensity: 1.48, opacity: 0.93, saturation: 0.9,
      bassGravity: 1.3, midCurl: 1.6, trebleShimmer: 1.5,
      cymaticStrength: 2.0, flowSpeed: 1.0, fieldTightness: 1.0,
      atmosphere: 0.64, bloomEnabled: true, bloomStrength: 1.22,
      bloomRadius: 0.64, bloomThreshold: 0.14,
      feedbackStrength: 0.035, feedbackDecay: 0.965,
    },
  },

  // ----------------------------------------------------------------
  // Living Forms — Immersiom-inspired cores that fill the gaps in the
  // math library: branching, cellular, fracturing and self-intersecting
  // topologies. All route to LiveMathParticleScene modes 8-15.
  // ----------------------------------------------------------------
  {
    name: "Mycelial Bloom",
    subtitle: "Branching rhizome with transient-fed filament tips",
    accent: 24,
    sceneType: "mycelialBloom",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 24, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.52, cameraZoom: 1.02, cameraSpin: 0.08,
      starSize: 0.19, intensity: 1.42, opacity: 0.93, saturation: 0.82,
      bassGravity: 1.35, midCurl: 1.1, trebleShimmer: 1.35,
      cymaticStrength: 1.2, flowSpeed: 0.9, fieldTightness: 1.0,
      atmosphere: 0.78, bloomEnabled: true, bloomStrength: 1.18,
      bloomRadius: 0.66, bloomThreshold: 0.13,
      mathParticleCount: 56_000, mathPointSize: 0.58, mathAmplitude: 0.62,
      mathFrequency: 1.2, mathRotationSpeed: 0.18, mathDepthScale: 0.62,
      mathWarmth: 0.96,
    },
    variants: [
      { name: "Spore Drift", accent: 44, values: { flowSpeed: 0.5, mathAmplitude: 0.9, trebleShimmer: 1.8 } },
      { name: "Deep Root", accent: 22, values: { paletteHue: 22, bassGravity: 1.8, mathFrequency: 0.6 } },
      { name: "Bioluminescent", accent: 168, values: { paletteHue: 168, mathWarmth: 0.2, bloomStrength: 1.3 } },
    ],
  },
  {
    name: "Chladni Shell",
    subtitle: "Standing-wave nodal figures that snap between modes",
    accent: 20,
    sceneType: "chladniShell",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 20, colorMode: 3, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 1.02, cameraSpin: 0.12,
      starSize: 0.2, intensity: 1.45, opacity: 0.94, saturation: 0.82,
      bassGravity: 1.5, midCurl: 1.45, trebleShimmer: 1.2,
      cymaticStrength: 1.8, flowSpeed: 0.8, fieldTightness: 1.0,
      atmosphere: 0.76, bloomEnabled: true, bloomStrength: 1.18,
      bloomRadius: 0.64, bloomThreshold: 0.12,
      mathParticleCount: 60_000, mathPointSize: 0.56, mathAmplitude: 0.7,
      mathFrequency: 1.6, mathRotationSpeed: 0.14, mathDepthScale: 0.5,
      mathWarmth: 0.96,
    },
    variants: [
      { name: "Salt Figure", accent: 46, values: { paletteHue: 46, mathWarmth: 0.82, saturation: 0.5 } },
      { name: "Brass Plate", accent: 34, values: { paletteHue: 34, mathFrequency: 2.6, bloomStrength: 1.25 } },
      { name: "Harmonic Lock", accent: 210, values: { midCurl: 2.0, mathAmplitude: 1.0, flowSpeed: 0.45 } },
    ],
  },
  {
    name: "Coral Skin",
    subtitle: "Reaction-diffusion membrane fed by the low end",
    accent: 14,
    sceneType: "coralSkin",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 14, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.54, cameraZoom: 1, cameraSpin: 0.09,
      starSize: 0.2, intensity: 1.4, opacity: 0.93, saturation: 0.84,
      bassGravity: 1.6, midCurl: 1.25, trebleShimmer: 1.0,
      cymaticStrength: 1.3, flowSpeed: 0.7, fieldTightness: 1.0,
      atmosphere: 0.8, bloomEnabled: true, bloomStrength: 1.15,
      bloomRadius: 0.66, bloomThreshold: 0.14,
      mathParticleCount: 64_000, mathPointSize: 0.58, mathAmplitude: 0.66,
      mathFrequency: 1.4, mathRotationSpeed: 0.12, mathDepthScale: 0.55,
      mathWarmth: 0.95,
    },
    variants: [
      { name: "Reef Bleach", accent: 40, values: { saturation: 0.32, mathWarmth: 0.9, bloomStrength: 0.7 } },
      { name: "Turing Tide", accent: 172, values: { paletteHue: 172, mathFrequency: 2.4, flowSpeed: 1.1 } },
      { name: "Calcite", accent: 210, values: { paletteHue: 210, mathWarmth: 0.16, mathAmplitude: 0.42 } },
    ],
  },
  {
    name: "Gyroid Lattice",
    subtitle: "Breathing triply-periodic minimal surface you can fly through",
    accent: 18,
    sceneType: "gyroidLattice",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 18, colorMode: 1, cameraMode: "flythrough",
      cameraTilt: 0.5, cameraZoom: 0.96, cameraSpin: 0.16,
      flythroughSpeed: 0.55, beatDolly: 0.24,
      starSize: 0.18, intensity: 1.46, opacity: 0.92, saturation: 0.8,
      bassGravity: 1.4, midCurl: 1.3, trebleShimmer: 1.1,
      cymaticStrength: 1.5, flowSpeed: 0.85, fieldTightness: 1.0,
      atmosphere: 0.78, bloomEnabled: true, bloomStrength: 1.22,
      bloomRadius: 0.68, bloomThreshold: 0.1,
      mathParticleCount: 72_000, mathPointSize: 0.52, mathAmplitude: 0.58,
      mathFrequency: 1.5, mathRotationSpeed: 0.2, mathDepthScale: 0.6,
      mathWarmth: 0.96,
    },
    variants: [
      { name: "Cathedral", accent: 44, values: { paletteHue: 44, mathWarmth: 0.86, mathFrequency: 0.8 } },
      { name: "Ice Scaffold", accent: 196, values: { paletteHue: 196, mathWarmth: 0.1, bloomStrength: 1.3 } },
      { name: "Collapse", accent: 350, values: { paletteHue: 350, bassGravity: 1.9, mathFrequency: 2.6 } },
    ],
  },
  {
    name: "Torus Knot Weave",
    subtitle: "(p,q) knot that re-ties itself on every downbeat",
    accent: 22,
    sceneType: "torusKnotWeave",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 22, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.56, cameraZoom: 0.98, cameraSpin: 0.2,
      starSize: 0.19, intensity: 1.44, opacity: 0.92, saturation: 0.82,
      bassGravity: 1.3, midCurl: 1.4, trebleShimmer: 1.4,
      cymaticStrength: 1.6, flowSpeed: 1.05, fieldTightness: 1.0,
      atmosphere: 0.76, bloomEnabled: true, bloomStrength: 1.18,
      bloomRadius: 0.66, bloomThreshold: 0.12,
      mathParticleCount: 58_000, mathPointSize: 0.54, mathAmplitude: 0.54,
      mathFrequency: 1.3, mathRotationSpeed: 0.24, mathDepthScale: 0.5,
      mathWarmth: 0.95,
    },
    variants: [
      { name: "Re-Tie", accent: 292, values: { flowSpeed: 1.6, mathRotationSpeed: 0.4 } },
      { name: "Trefoil Lock", accent: 258, values: { mathFrequency: 0.5, flowSpeed: 0.4, mathAmplitude: 0.8 } },
      { name: "Silk Braid", accent: 40, values: { paletteHue: 40, mathWarmth: 0.88, trebleShimmer: 1.9 } },
    ],
  },
  {
    name: "Cymatic Bell",
    subtitle: "Rayleigh water bell with beat-rate ripple rings",
    accent: 30,
    sceneType: "cymaticBell",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 30, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.46, cameraZoom: 1, cameraSpin: 0.1,
      starSize: 0.2, intensity: 1.44, opacity: 0.94, saturation: 0.78,
      bassGravity: 1.55, midCurl: 1.35, trebleShimmer: 1.2,
      cymaticStrength: 1.7, flowSpeed: 1.0, fieldTightness: 1.0,
      atmosphere: 0.78, bloomEnabled: true, bloomStrength: 1.18,
      bloomRadius: 0.64, bloomThreshold: 0.12,
      mathParticleCount: 60_000, mathPointSize: 0.58, mathAmplitude: 0.68,
      mathFrequency: 1.5, mathRotationSpeed: 0.16, mathDepthScale: 0.58,
      mathWarmth: 0.96,
    },
    variants: [
      { name: "Nozzle Bloom", accent: 52, values: { bassGravity: 1.9, mathAmplitude: 0.95 } },
      { name: "Mercury Fall", accent: 205, values: { paletteHue: 205, mathWarmth: 0.12, saturation: 0.42 } },
      { name: "Rim Fire", accent: 14, values: { paletteHue: 14, bloomStrength: 1.35, trebleShimmer: 1.8 } },
    ],
  },
  {
    name: "Voronoi Shatter",
    subtitle: "Crystal cells burst on transients and re-seat between hits",
    accent: 12,
    sceneType: "voronoiShatter",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 12, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 1, cameraSpin: 0.14,
      starSize: 0.2, intensity: 1.55, opacity: 0.94, saturation: 0.86,
      bassGravity: 1.7, midCurl: 1.2, trebleShimmer: 1.3,
      cymaticStrength: 1.4, flowSpeed: 1.0, fieldTightness: 1.0,
      atmosphere: 0.76, bloomEnabled: true, bloomStrength: 1.25,
      bloomRadius: 0.68, bloomThreshold: 0.09,
      mathParticleCount: 62_000, mathPointSize: 0.58, mathAmplitude: 0.6,
      mathFrequency: 1.4, mathRotationSpeed: 0.18, mathDepthScale: 0.55,
      mathWarmth: 0.96,
    },
    variants: [
      { name: "Kiln Crack", accent: 24, values: { paletteHue: 24, mathWarmth: 0.9, bloomStrength: 1.3 } },
      { name: "Obsidian", accent: 232, values: { paletteHue: 232, saturation: 0.5, mathWarmth: 0.1 } },
      { name: "Slow Reassembly", accent: 300, values: { flowSpeed: 0.45, mathFrequency: 0.7, bassGravity: 2.0 } },
    ],
  },
  {
    name: "Klein Ribbon",
    subtitle: "Figure-8 Klein immersion with a travelling surface band",
    accent: 18,
    sceneType: "kleinRibbon",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 18, colorMode: 3, cameraMode: "orbit",
      cameraTilt: 0.55, cameraZoom: 1, cameraSpin: 0.11,
      starSize: 0.19, intensity: 1.38, opacity: 0.92, saturation: 0.78,
      bassGravity: 1.25, midCurl: 1.3, trebleShimmer: 1.15,
      cymaticStrength: 1.2, flowSpeed: 0.65, fieldTightness: 1.0,
      atmosphere: 0.8, bloomEnabled: true, bloomStrength: 1.14,
      bloomRadius: 0.66, bloomThreshold: 0.14,
      mathParticleCount: 56_000, mathPointSize: 0.54, mathAmplitude: 0.56,
      mathFrequency: 1.2, mathRotationSpeed: 0.13, mathDepthScale: 0.52,
      mathWarmth: 0.95,
    },
    variants: [
      { name: "Self-Passing", accent: 140, values: { flowSpeed: 1.2, mathFrequency: 2.2 } },
      { name: "Glass Loop", accent: 190, values: { paletteHue: 190, saturation: 0.4, mathWarmth: 0.14 } },
      { name: "Mobius Dawn", accent: 30, values: { paletteHue: 30, mathWarmth: 0.9, bloomStrength: 1.2 } },
    ],
  },

  // ----------------------------------------------------------------
  // Living Forms wave 2 — motion signatures the first twelve cores do
  // not cover: a surface instability, a bound field-line cage, a
  // chaotic attractor, and a shedding vortex train.
  // ----------------------------------------------------------------
  {
    name: "Ferrofluid Crown",
    subtitle: "Liquid metal climbing into a spike lattice on the low end",
    accent: 26,
    sceneType: "ferrofluidCrown",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 26, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.62, cameraZoom: 1.02, cameraSpin: 0.07,
      starSize: 0.2, intensity: 1.34, opacity: 0.94, saturation: 0.85,
      bassGravity: 1.75, midCurl: 1.1, trebleShimmer: 1.25,
      cymaticStrength: 1.6, flowSpeed: 0.72, fieldTightness: 1.0,
      atmosphere: 0.78, bloomEnabled: true, bloomStrength: 1.14,
      bloomRadius: 0.66, bloomThreshold: 0.17,
      mathParticleCount: 64_000, mathPointSize: 0.56, mathAmplitude: 0.72,
      mathFrequency: 1.3, mathRotationSpeed: 0.1, mathDepthScale: 0.68,
      mathWarmth: 0.96,
    },
    variants: [
      { name: "Molten", accent: 12, values: { paletteHue: 12, bassGravity: 2.1, mathAmplitude: 1.0, bloomStrength: 1.34 } },
      { name: "Chrome Pool", accent: 208, values: { paletteHue: 208, saturation: 0.38, mathWarmth: 0.12, mathAmplitude: 0.5 } },
      { name: "Fine Needles", accent: 44, values: { mathFrequency: 2.6, trebleShimmer: 1.8, mathPointSize: 0.48 } },
    ],
  },
  {
    name: "Magnetic Curtain",
    subtitle: "Standing auroral sheets striated into field-aligned rays",
    accent: 158,
    sceneType: "magneticCurtain",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 158, colorMode: 3, cameraMode: "orbit",
      cameraTilt: 0.44, cameraZoom: 0.98, cameraSpin: 0.09,
      starSize: 0.17, intensity: 1.4, opacity: 0.92, saturation: 0.8,
      bassGravity: 1.45, midCurl: 1.5, trebleShimmer: 1.45,
      cymaticStrength: 1.3, flowSpeed: 0.78, fieldTightness: 1.0,
      atmosphere: 0.82, bloomEnabled: true, bloomStrength: 1.2,
      bloomRadius: 0.7, bloomThreshold: 0.12,
      mathParticleCount: 68_000, mathPointSize: 0.5, mathAmplitude: 0.8,
      mathFrequency: 1.4, mathRotationSpeed: 0.12, mathDepthScale: 0.46,
      mathWarmth: 0.24, mathColorSpread: 0.42, mathColorFlow: 0.9,
    },
    variants: [
      { name: "Substorm", accent: 300, values: { bassGravity: 1.9, midCurl: 2.0, bloomStrength: 1.32, flowSpeed: 1.15 } },
      { name: "Polar Night", accent: 196, values: { paletteHue: 196, mathColorSpread: 0.14, atmosphere: 0.62, saturation: 0.66 } },
      { name: "Solar Flare", accent: 22, values: { paletteHue: 22, mathWarmth: 0.94, mathColorSpread: 0.2, trebleShimmer: 1.8 } },
    ],
  },
  {
    name: "Fractal Flame",
    subtitle: "Three-map attractor breathing through swirl and sphere folds",
    accent: 16,
    sceneType: "fractalFlame",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 16, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 1.0, cameraSpin: 0.15,
      starSize: 0.16, intensity: 1.36, opacity: 0.9, saturation: 0.86,
      bassGravity: 1.4, midCurl: 1.55, trebleShimmer: 1.3,
      cymaticStrength: 1.4, flowSpeed: 0.6, fieldTightness: 1.0,
      atmosphere: 0.74, bloomEnabled: true, bloomStrength: 1.12,
      bloomRadius: 0.68, bloomThreshold: 0.18,
      mathParticleCount: 72_000, mathPointSize: 0.42, mathAmplitude: 0.62,
      mathFrequency: 1.2, mathRotationSpeed: 0.16, mathDepthScale: 0.6,
      mathWarmth: 0.94, mathColorSpread: 0.3,
    },
    variants: [
      { name: "Ash Spiral", accent: 34, values: { midCurl: 2.2, flowSpeed: 0.95, mathFrequency: 0.7 } },
      { name: "Cold Fire", accent: 214, values: { paletteHue: 214, mathWarmth: 0.12, mathColorSpread: 0.5, saturation: 0.72 } },
      { name: "Gasket", accent: 292, values: { paletteHue: 292, midCurl: 0.5, trebleShimmer: 0.6, mathFrequency: 0.4, flowSpeed: 0.3 } },
    ],
  },
  {
    name: "Vortex Cascade",
    subtitle: "Smoke rings shed from a nozzle and roll outward as they age",
    accent: 190,
    sceneType: "vortexCascade",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 190, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.48, cameraZoom: 1.0, cameraSpin: 0.08,
      starSize: 0.18, intensity: 1.44, opacity: 0.92, saturation: 0.8,
      bassGravity: 1.6, midCurl: 1.4, trebleShimmer: 1.35,
      cymaticStrength: 1.5, flowSpeed: 0.9, fieldTightness: 1.0,
      atmosphere: 0.8, bloomEnabled: true, bloomStrength: 1.2,
      bloomRadius: 0.68, bloomThreshold: 0.12,
      mathParticleCount: 66_000, mathPointSize: 0.54, mathAmplitude: 0.66,
      mathFrequency: 1.3, mathRotationSpeed: 0.1, mathDepthScale: 0.58,
      mathWarmth: 0.3, mathColorSpread: 0.36, mathColorFlow: 0.8,
    },
    variants: [
      { name: "Steam Room", accent: 34, values: { paletteHue: 34, mathWarmth: 0.9, mathColorSpread: 0.16, atmosphere: 0.95 } },
      { name: "Rapid Fire", accent: 320, values: { flowSpeed: 1.6, bassGravity: 2.0, mathAmplitude: 0.9 } },
      { name: "Slow Exhale", accent: 168, values: { flowSpeed: 0.4, mathAmplitude: 1.05, trebleShimmer: 0.7 } },
    ],
  },
  {
    name: "Smoke Column",
    subtitle: "A buoyant plume curling through divergence-free turbulence",
    accent: 30,
    sceneType: "smokeColumn",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 30, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 1.0, cameraSpin: 0.06,
      starSize: 0.19, intensity: 1.3, opacity: 0.9, saturation: 0.58,
      bassGravity: 1.65, midCurl: 1.5, trebleShimmer: 1.3,
      cymaticStrength: 1.3, flowSpeed: 0.7, fieldTightness: 1.0,
      atmosphere: 0.95, bloomEnabled: true, bloomStrength: 1.1,
      bloomRadius: 0.72, bloomThreshold: 0.2,
      mathParticleCount: 70_000, mathPointSize: 0.62, mathAmplitude: 0.7,
      mathFrequency: 1.2, mathRotationSpeed: 0.08, mathDepthScale: 0.62,
      mathWarmth: 0.72, mathColorSpread: 0.18, mathColorFlow: 0.5,
    },
    variants: [
      { name: "Ember Stack", accent: 14, values: { paletteHue: 14, saturation: 0.82, mathWarmth: 0.95, bloomStrength: 1.26 } },
      { name: "Cold Vapour", accent: 200, values: { paletteHue: 200, mathWarmth: 0.12, saturation: 0.44, midCurl: 1.9 } },
      { name: "Downdraft", accent: 268, values: { bassGravity: 2.2, midCurl: 2.2, flowSpeed: 1.4, mathAmplitude: 1.0 } },
    ],
  },
  {
    name: "Harmonograph",
    subtitle: "Damped pendulums drawing rosettes that re-lock to musical intervals",
    accent: 196,
    sceneType: "harmonograph",
    controlProfile: "mathParticles",
    mood: "Living Forms",
    values: {
      paletteHue: 196, colorMode: 3, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 1.0, cameraSpin: 0.1,
      starSize: 0.15, intensity: 1.34, opacity: 0.9, saturation: 0.78,
      bassGravity: 1.3, midCurl: 1.3, trebleShimmer: 1.2,
      cymaticStrength: 1.2, flowSpeed: 0.55, fieldTightness: 1.0,
      atmosphere: 0.7, bloomEnabled: true, bloomStrength: 1.1,
      bloomRadius: 0.62, bloomThreshold: 0.2,
      mathParticleCount: 66_000, mathPointSize: 0.55, mathAmplitude: 0.66,
      mathFrequency: 1.2, mathRotationSpeed: 0.08, mathDepthScale: 0.54,
      mathWarmth: 0.28, mathColorSpread: 0.4, mathColorFlow: 0.6,
    },
    variants: [
      { name: "Wide Swing", accent: 286, values: { mathFrequency: 0.4, mathAmplitude: 1.05, flowSpeed: 0.35 } },
      { name: "Brass Plate", accent: 36, values: { paletteHue: 36, mathWarmth: 0.92, mathColorSpread: 0.14, saturation: 0.7 } },
      { name: "Dense Weave", accent: 152, values: { mathFrequency: 2.8, mathPointSize: 0.32, trebleShimmer: 1.7 } },
    ],
  },
  {
    name: "Foam Party",
    subtitle: "Iridescent bubbles rising over a beat-driven floor of foam",
    accent: 192,
    sceneType: "foamParty",
    controlProfile: "mathParticles",
    mood: "Hype",
    values: {
      paletteHue: 192, colorMode: 3, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 1.0, cameraSpin: 0.015,
      starSize: 0.2, intensity: 1.18, opacity: 0.92, saturation: 0.72,
      bassGravity: 1.55, midCurl: 1.15, trebleShimmer: 1.5,
      cymaticStrength: 1.2, flowSpeed: 0.82, fieldTightness: 1.0,
      atmosphere: 0.42, bloomEnabled: false, bloomStrength: 0,
      bloomRadius: 0.48, bloomThreshold: 0.64,
      mathParticleCount: 34_000, mathPointSize: 0.95, mathAmplitude: 0.86,
      mathFrequency: 1.25, mathRotationSpeed: 0.015, mathDepthScale: 0.78,
      mathWarmth: 0.12, mathColorSpread: 0.9, mathColorFlow: 0.7,
    },
    variants: [
      {
        name: "Foam Flood",
        accent: 205,
        values: {
          mathParticleCount: 52_000, mathPointSize: 1.15, opacity: 0.96,
          bassGravity: 1.9, mathAmplitude: 1.15, flowSpeed: 0.64,
        },
      },
      {
        name: "Bubblegum",
        accent: 326,
        values: {
          paletteHue: 326, saturation: 0.82, mathWarmth: 0.42,
          mathColorSpread: 0.68, trebleShimmer: 1.8,
        },
      },
      {
        name: "Champagne",
        accent: 44,
        values: {
          paletteHue: 44, saturation: 0.58, mathWarmth: 0.78,
          mathColorSpread: 0.5, flowSpeed: 1.18, bloomStrength: 0.82,
        },
      },
    ],
  },

  // ----------------------------------------------------------------
  // Math Based — GPGPU billboard variants of the four flagship math
  // pads above. These route to SuperformGPUScene (engine-webgl) for a
  // denser nebula-like alternative aesthetic.
  // ----------------------------------------------------------------
  {
    name: "Superform (GPU)",
    subtitle: "GPGPU billboard Gielis-morph nebula",
    accent: 200,
    sceneType: "superformGPU",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 196, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.52, cameraZoom: 0.88, cameraSpin: 0.32,
      starSize: 0.24, intensity: 1.5, opacity: 0.95, saturation: 0.9,
      bassGravity: 1.25, midCurl: 1.8, trebleShimmer: 1.4,
      cymaticStrength: 2.1, flowSpeed: 0.95, fieldTightness: 1.0,
      atmosphere: 0.62, bloomEnabled: true, bloomStrength: 1.15,
      bloomRadius: 0.62, bloomThreshold: 0.14,
      feedbackStrength: 0.035, feedbackDecay: 0.965,
    },
  },
  {
    name: "Hopf Fibration (GPU)",
    subtitle: "GPGPU billboard Hopf bundle",
    accent: 258,
    sceneType: "hopfFibrationGPU",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 258, colorMode: 3, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 0.78, cameraSpin: 0.26,
      starSize: 0.21, intensity: 1.42, opacity: 0.92, saturation: 0.88,
      bassGravity: 1.15, midCurl: 1.5, trebleShimmer: 1.7,
      cymaticStrength: 1.9, flowSpeed: 0.82, fieldTightness: 1.0,
      atmosphere: 0.66, bloomEnabled: true, bloomStrength: 1.18,
      bloomRadius: 0.68, bloomThreshold: 0.14,
      feedbackStrength: 0.03, feedbackDecay: 0.968,
    },
  },
  {
    name: "Fibonacci Sequence (GPU)",
    subtitle: "GPGPU billboard accretion disc / phyllotaxis",
    accent: 42,
    sceneType: "fibonacciSequenceGPU",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 42, colorMode: 1, cameraMode: "orbit",
      cameraTilt: 0.58, cameraZoom: 0.9, cameraSpin: 0.18,
      starSize: 0.2, intensity: 1.34, opacity: 0.94, saturation: 0.86,
      bassGravity: 1.05, midCurl: 1.25, trebleShimmer: 1.55,
      cymaticStrength: 1.55, flowSpeed: 0.88, fieldTightness: 1.0,
      atmosphere: 0.58, bloomEnabled: true, bloomStrength: 1.05,
      bloomRadius: 0.58, bloomThreshold: 0.16,
      feedbackStrength: 0.025, feedbackDecay: 0.97,
    },
  },
  {
    name: "Radial Pulse Field (GPU)",
    subtitle: "GPGPU billboard concentric radial shells",
    accent: 14,
    sceneType: "radialPulseGPU",
    controlProfile: "mathParticles",
    mood: "Math Based",
    values: {
      paletteHue: 14, colorMode: 2, cameraMode: "orbit",
      cameraTilt: 0.5, cameraZoom: 0.85, cameraSpin: 0.18,
      starSize: 0.22, intensity: 1.45, opacity: 0.94, saturation: 0.9,
      bassGravity: 1.7, midCurl: 1.2, trebleShimmer: 1.1,
      cymaticStrength: 1.9, flowSpeed: 1.0, fieldTightness: 1.05,
      atmosphere: 0.6, bloomEnabled: true, bloomStrength: 1.2,
      bloomRadius: 0.62, bloomThreshold: 0.14,
      feedbackStrength: 0.03, feedbackDecay: 0.965,
    },
  },

  // ----------------------------------------------------------------
  // Alt-engine curated scenes — one per orphan AltScene engine so the
  // engine is reachable from the Curated gallery. Each routes via
  // `sceneType` and starts on a flattering preset for that renderer.
  // ----------------------------------------------------------------
  {
    name: "Wireframe Terrain",
    subtitle: "Mesh Grid engine — perlin landscape with audio displacement",
    accent: 168,
    sceneType: "meshGrid",
    controlProfile: "mesh",
    mood: "Tech",
    values: {
      paletteHue: 168,
      colorMode: 1,
      cameraMode: "orbit",
      cameraTilt: 0.62,
      cameraZoom: 0.78,
      cameraSpin: 0.14,
      starSize: 0.18,
      intensity: 1.35,
      opacity: 0.94,
      saturation: 0.85,
      bassGravity: 1.5,
      midCurl: 1.2,
      trebleShimmer: 1.0,
      cymaticStrength: 1.4,
      flowSpeed: 0.85,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: true,
      bloomStrength: 1.05,
      bloomRadius: 0.55,
      bloomThreshold: 0.18,
    },
  },
  {
    name: "Wavy Form",
    subtitle: "Stacked rotating waveform polylines",
    accent: 290,
    sceneType: "wavyForm",
    controlProfile: "mathParticles",
    mood: "Dreamy",
    values: {
      paletteHue: 290,
      colorMode: 3,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.85,
      cameraSpin: 0.22,
      starSize: 0.2,
      intensity: 1.4,
      opacity: 0.95,
      saturation: 0.92,
      bassGravity: 1.2,
      midCurl: 1.6,
      trebleShimmer: 1.5,
      cymaticStrength: 1.7,
      flowSpeed: 0.95,
      fieldTightness: 1.0,
      atmosphere: 0.6,
      bloomEnabled: true,
      bloomStrength: 1.2,
      bloomRadius: 0.6,
      bloomThreshold: 0.14,
    },
  },
  {
    name: "Dot Wall",
    subtitle: "Instanced dot wall, audio-displaced",
    accent: 320,
    sceneType: "dotGrid",
    controlProfile: "mathParticles",
    mood: "Tech",
    values: {
      paletteHue: 320,
      colorMode: 2,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 0.9,
      cameraSpin: 0.12,
      starSize: 0.22,
      intensity: 1.5,
      opacity: 0.96,
      saturation: 0.95,
      bassGravity: 1.45,
      midCurl: 1.3,
      trebleShimmer: 1.4,
      cymaticStrength: 1.6,
      flowSpeed: 0.9,
      fieldTightness: 1.0,
      atmosphere: 0.5,
      bloomEnabled: true,
      bloomStrength: 1.15,
      bloomRadius: 0.55,
      bloomThreshold: 0.16,
    },
  },
  {
    name: "Frequency Hallway",
    subtitle: "Mirrored stacked spectrum bars",
    accent: 12,
    sceneType: "frequencyBars",
    controlProfile: "mathParticles",
    mood: "Hype",
    values: {
      paletteHue: 12,
      colorMode: 2,
      cameraMode: "orbit",
      cameraTilt: 0.45,
      cameraZoom: 0.8,
      cameraOrbit: 1.5708,
      cameraSpin: 0.02,
      starSize: 0.22,
      intensity: 1.55,
      opacity: 0.96,
      saturation: 0.95,
      bassGravity: 1.7,
      midCurl: 1.4,
      trebleShimmer: 1.3,
      cymaticStrength: 1.8,
      flowSpeed: 0.95,
      fieldTightness: 1.0,
      atmosphere: 0.55,
      bloomEnabled: false,
      bloomStrength: 0.62,
      bloomRadius: 0.45,
      bloomThreshold: 0.38,
    },
  },
  {
    name: "Spiral Galaxy",
    subtitle: "Galaxy engine — spiral arms, audio-pulsing core",
    accent: 230,
    sceneType: "galaxy",
    controlProfile: "mathParticles",
    mood: "Cosmic",
    values: {
      paletteHue: 230,
      colorMode: 3,
      cameraMode: "orbit",
      cameraTilt: 0.55,
      cameraZoom: 0.75,
      cameraSpin: 0.18,
      starSize: 0.24,
      intensity: 1.55,
      opacity: 0.94,
      saturation: 0.9,
      bassGravity: 1.4,
      midCurl: 1.3,
      trebleShimmer: 1.6,
      cymaticStrength: 1.9,
      flowSpeed: 0.92,
      fieldTightness: 1.0,
      atmosphere: 0.7,
      bloomEnabled: true,
      bloomStrength: 0.68,
      bloomRadius: 0.5,
      bloomThreshold: 0.36,
      feedbackStrength: 0.018,
      feedbackDecay: 0.965,
    },
  },
  {
    name: "Cellular Matter",
    subtitle: "Living gyroid membranes divide and reopen with the music",
    accent: 142,
    sceneType: "flowFormGPU",
    controlProfile: "flowForm",
    mood: "Math Based",
    values: {
      flowFormLook: "cellular-matter",
      paletteHue: 142,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.08,
      cameraSpin: 0.08,
      starSize: 0.3,
      intensity: 1.55,
      opacity: 0.96,
      saturation: 0.94,
      bassGravity: 1.5,
      midCurl: 1.25,
      trebleShimmer: 1.05,
      cymaticStrength: 1.4,
      flowSpeed: 0.9,
      fieldTightness: 1,
      atmosphere: 0.52,
      bloomEnabled: true,
      bloomStrength: 0.36,
      bloomRadius: 0.2,
      bloomThreshold: 0.68,
    },
  },
  {
    name: "Neural Field",
    subtitle: "Axial currents stream through branching synaptic filaments",
    accent: 274,
    sceneType: "flowFormGPU",
    controlProfile: "flowForm",
    mood: "Math Based",
    values: {
      flowFormLook: "neural-field",
      paletteHue: 274,
      cameraMode: "orbit",
      cameraTilt: 0.48,
      cameraZoom: 0.92,
      cameraSpin: 0.04,
      starSize: 0.25,
      intensity: 1.62,
      opacity: 0.97,
      saturation: 0.9,
      bassGravity: 0.9,
      midCurl: 1.55,
      trebleShimmer: 1.7,
      cymaticStrength: 0.8,
      flowSpeed: 1.18,
      fieldTightness: 1.08,
      atmosphere: 0.44,
      bloomEnabled: true,
      bloomStrength: 0.32,
      bloomRadius: 0.18,
      bloomThreshold: 0.72,
    },
  },
  {
    name: "Infinite Petri",
    subtitle: "Hollow colonies drift, separate, and reform in negative space",
    accent: 24,
    sceneType: "flowFormGPU",
    controlProfile: "flowForm",
    mood: "Math Based",
    values: {
      flowFormLook: "infinite-petri",
      paletteHue: 24,
      cameraMode: "orbit",
      cameraTilt: 0.52,
      cameraZoom: 0.92,
      cameraSpin: 0.06,
      starSize: 0.28,
      intensity: 1.58,
      opacity: 0.96,
      saturation: 0.96,
      bassGravity: 1.35,
      midCurl: 1.15,
      trebleShimmer: 1.05,
      cymaticStrength: 1.25,
      flowSpeed: 0.82,
      fieldTightness: 1.02,
      atmosphere: 0.5,
      bloomEnabled: true,
      bloomStrength: 0.36,
      bloomRadius: 0.2,
      bloomThreshold: 0.68,
    },
  },
  {
    name: "Coherent Fold",
    subtitle: "A particle body opens an aperture, releases, and rejoins",
    accent: 318,
    sceneType: "flowFormGPU",
    controlProfile: "flowForm",
    mood: "Math Based",
    values: {
      flowFormLook: "coherent-fold",
      paletteHue: 318,
      cameraMode: "orbit",
      cameraTilt: 0.54,
      cameraZoom: 1.1,
      cameraSpin: 0.1,
      starSize: 0.3,
      intensity: 1.65,
      opacity: 0.97,
      saturation: 0.94,
      bassGravity: 1.7,
      midCurl: 1.1,
      trebleShimmer: 1,
      cymaticStrength: 1.75,
      flowSpeed: 0.72,
      fieldTightness: 0.96,
      atmosphere: 0.48,
      bloomEnabled: true,
      bloomStrength: 0.4,
      bloomRadius: 0.22,
      bloomThreshold: 0.66,
    },
  },
  {
    name: "Cymatic Current",
    subtitle: "Traveling pressure waves cross a circulating nodal shell",
    accent: 192,
    sceneType: "flowFormGPU",
    controlProfile: "flowForm",
    mood: "Math Based",
    values: {
      flowFormLook: "cymatic-current",
      paletteHue: 192,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.06,
      cameraSpin: 0.08,
      starSize: 0.29,
      intensity: 1.62,
      opacity: 0.97,
      saturation: 0.94,
      bassGravity: 1.45,
      midCurl: 1.2,
      trebleShimmer: 1.2,
      cymaticStrength: 1.9,
      flowSpeed: 1.05,
      fieldTightness: 1,
      atmosphere: 0.5,
      bloomEnabled: true,
      bloomStrength: 0.4,
      bloomRadius: 0.22,
      bloomThreshold: 0.66,
    },
  },
  {
    name: "Scale Lattice",
    subtitle: "A three-axis particle lattice bends without becoming a cage",
    accent: 48,
    sceneType: "flowFormGPU",
    controlProfile: "flowForm",
    mood: "Math Based",
    values: {
      flowFormLook: "scale-lattice",
      paletteHue: 48,
      cameraMode: "orbit",
      cameraTilt: 0.58,
      cameraZoom: 0.9,
      cameraSpin: 0.035,
      starSize: 0.23,
      intensity: 1.7,
      opacity: 0.98,
      saturation: 0.9,
      bassGravity: 0.8,
      midCurl: 1.55,
      trebleShimmer: 1.45,
      cymaticStrength: 0.75,
      flowSpeed: 0.78,
      fieldTightness: 1.08,
      atmosphere: 0.4,
      bloomEnabled: true,
      bloomStrength: 0.3,
      bloomRadius: 0.16,
      bloomThreshold: 0.74,
    },
  },
  {
    name: "Deep Field",
    subtitle: "Nested depth strata drift through cool volumetric space",
    accent: 232,
    sceneType: "flowFormGPU",
    controlProfile: "flowForm",
    mood: "Math Based",
    values: {
      flowFormLook: "deep-field",
      paletteHue: 232,
      cameraMode: "orbit",
      cameraTilt: 0.46,
      cameraZoom: 0.82,
      cameraSpin: 0.025,
      starSize: 0.25,
      intensity: 1.72,
      opacity: 0.98,
      saturation: 0.88,
      bassGravity: 1.15,
      midCurl: 0.9,
      trebleShimmer: 1.25,
      cymaticStrength: 0.85,
      flowSpeed: 0.72,
      fieldTightness: 1.12,
      atmosphere: 0.54,
      bloomEnabled: true,
      bloomStrength: 0.34,
      bloomRadius: 0.2,
      bloomThreshold: 0.7,
    },
  },
  {
    name: "Ramsey Flow",
    subtitle: "Ordered instability weaves quasiperiodic topological lanes",
    accent: 300,
    sceneType: "flowFormGPU",
    controlProfile: "flowForm",
    mood: "Math Based",
    values: {
      flowFormLook: "ramsey-flow",
      paletteHue: 300,
      cameraMode: "orbit",
      cameraTilt: 0.56,
      cameraZoom: 0.88,
      cameraSpin: 0.055,
      starSize: 0.24,
      intensity: 1.68,
      opacity: 0.98,
      saturation: 0.96,
      bassGravity: 1,
      midCurl: 1.7,
      trebleShimmer: 1.35,
      cymaticStrength: 0.9,
      flowSpeed: 1.12,
      fieldTightness: 1.04,
      atmosphere: 0.44,
      bloomEnabled: true,
      bloomStrength: 0.34,
      bloomRadius: 0.18,
      bloomThreshold: 0.7,
    },
  },
  {
    name: "Sanctuary Lasers",
    subtitle: "Projection-mapped lasers tracing every Gothic arch, buttress, and spire",
    accent: 192,
    sceneType: "sanctuaryLasers",
    mood: "Showcase",
    values: {
      paletteHue: 192,
      colorMode: 2,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.0,
      cameraSpin: 0,
      intensity: 1.45,
      opacity: 0.96,
      saturation: 0.95,
      bassGravity: 1.6,
      midCurl: 1.4,
      trebleShimmer: 1.5,
      cymaticStrength: 1.6,
      flowSpeed: 1.0,
      bloomEnabled: true,
      bloomStrength: 0.85,
      bloomRadius: 0.45,
      bloomThreshold: 0.28,
      feedbackStrength: 0.12,
      feedbackDecay: 0.95,
    },
    variants: [
      { name: "Neon Cyber", accent: 310, values: { paletteHue: 237, saturation: 1.0, bloomStrength: 1.05 } },
      { name: "Golden Sanctuary", accent: 42, values: { paletteHue: 27, saturation: 0.82, intensity: 1.58 } },
      { name: "Emerald Vault", accent: 148, values: { paletteHue: 142, saturation: 0.92 } },
    ],
  },
  {
    name: "Stained Glass Awakening",
    subtitle: "Cathedral lancets and rose oculi blazing with living stained glass",
    accent: 38,
    sceneType: "stainedGlassAwakening",
    mood: "Cinematic",
    values: {
      paletteHue: 38,
      colorMode: 1,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.0,
      cameraSpin: 0,
      intensity: 1.45,
      opacity: 0.96,
      saturation: 0.94,
      bassGravity: 1.5,
      midCurl: 1.35,
      trebleShimmer: 1.4,
      cymaticStrength: 1.45,
      flowSpeed: 0.95,
      bloomEnabled: true,
      bloomStrength: 0.72,
      bloomRadius: 0.42,
      bloomThreshold: 0.32,
      feedbackStrength: 0.08,
      feedbackDecay: 0.96,
    },
    variants: [
      { name: "Ruby Rose", accent: 348, values: { paletteHue: 358, saturation: 1.0 } },
      { name: "Chartres Blue", accent: 215, values: { paletteHue: 203, saturation: 0.95 } },
      { name: "Amber Reliquary", accent: 44, values: { paletteHue: 56, saturation: 0.82, intensity: 1.55 } },
    ],
  },
  {
    name: "Cathedral Organ",
    subtitle: "Seven spire towers playing harmonic columns and cymatic windows",
    accent: 28,
    sceneType: "cathedralOrgan",
    mood: "Hype",
    values: {
      paletteHue: 28,
      colorMode: 2,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.0,
      cameraSpin: 0,
      intensity: 1.5,
      opacity: 0.96,
      saturation: 0.92,
      bassGravity: 1.75,
      midCurl: 1.5,
      trebleShimmer: 1.45,
      cymaticStrength: 1.7,
      flowSpeed: 1.05,
      bloomEnabled: true,
      bloomStrength: 0.78,
      bloomRadius: 0.44,
      bloomThreshold: 0.3,
      feedbackStrength: 0.1,
      feedbackDecay: 0.955,
    },
    variants: [
      { name: "Pipe Harmonics", accent: 190, values: { paletteHue: 168, saturation: 0.96 } },
      { name: "Brass Choir", accent: 42, values: { paletteHue: 40, saturation: 0.8, intensity: 1.58 } },
    ],
  },
  {
    name: "Celestial Ascension",
    subtitle: "Portal shockwaves and rising embers ascending the church facade",
    accent: 220,
    sceneType: "celestialAscension",
    mood: "Cosmic",
    values: {
      paletteHue: 220,
      colorMode: 3,
      cameraMode: "orbit",
      cameraTilt: 0.5,
      cameraZoom: 1.0,
      cameraSpin: 0,
      intensity: 1.48,
      opacity: 0.96,
      saturation: 0.92,
      bassGravity: 1.65,
      midCurl: 1.45,
      trebleShimmer: 1.55,
      cymaticStrength: 1.6,
      flowSpeed: 1.0,
      bloomEnabled: true,
      bloomStrength: 0.82,
      bloomRadius: 0.48,
      bloomThreshold: 0.28,
      feedbackStrength: 0.12,
      feedbackDecay: 0.96,
    },
    variants: [
      { name: "Solar Portal", accent: 36, values: { paletteHue: 40, saturation: 0.9 } },
      { name: "Astral Choir", accent: 276, values: { paletteHue: 295, saturation: 0.98 } },
    ],
  },
];

// ---------------------------------------------------------------------------
// Mood + variant overlays
// ---------------------------------------------------------------------------
//
// Tagging existing scenes with a mood + a couple of variants is done as a
// post-pass keyed by name so we don't have to rewrite the 1200-line literal
// above. New scenes added in the future can declare `mood`/`variants`
// inline and skip this table entirely.
const SCENE_OVERLAYS: Record<
  string,
  { mood?: SceneMood; variants?: SceneVariant[] }
> = {
  // ------------- Universal-appeal defaults -------------
  "Liquid Mass": {
    mood: "Chill",
    variants: [
      { name: "Warm", accent: 25, values: { colorMode: 2, saturation: 0.95, atmosphere: 0.7 } },
      { name: "Hot",  accent: 0,  values: { colorMode: 2, intensity: 1.7, bassGravity: 1.8, bloomStrength: 1.2 } },
      { name: "Mono", accent: 210, values: { colorMode: 0, saturation: 0.55, atmosphere: 0.5 } },
    ],
  },
  Aurora: {
    mood: "Dreamy",
    variants: [
      { name: "Polar",  accent: 195, values: { colorMode: 3, trebleShimmer: 1.9, atmosphere: 0.8 } },
      { name: "Sunset", accent: 25,  values: { colorMode: 2, saturation: 1.0, intensity: 1.3 } },
      { name: "Violet", accent: 285, values: { colorMode: 1, saturation: 0.95 } },
    ],
  },
  "Warp Drive": {
    mood: "Hype",
    variants: [
      { name: "Chrome", accent: 200, values: { colorMode: 0, saturation: 0.5 } },
      { name: "Neon",  accent: 320, values: { colorMode: 1, intensity: 1.8, bloomStrength: 1.7 } },
      { name: "Pulse", accent: 215, values: { beatDolly: 1.4, flowSpeed: 3.2 } },
    ],
  },
  Cathedral: {
    mood: "Cinematic",
    variants: [
      { name: "Stone",  accent: 45,  values: { colorMode: 2, saturation: 0.6, atmosphere: 0.5 } },
      { name: "Aqua",   accent: 175, values: { colorMode: 3, saturation: 0.9 } },
      { name: "Crimson", accent: 355, values: { colorMode: 2, saturation: 0.95, intensity: 1.45 } },
    ],
  },

  // ------------- Mood / quiet -------------
  "Ghost Cloud": {
    mood: "Dreamy",
    variants: [
      { name: "Mist", accent: 215, values: { atmosphere: 0.55, opacity: 0.65 } },
      { name: "Rose", accent: 340, values: { colorMode: 2, saturation: 0.85 } },
    ],
  },
  Nebula: {
    mood: "Cosmic",
    variants: [
      { name: "Deep",   accent: 245, values: { saturation: 0.6, atmosphere: 0.55 } },
      { name: "Plasma", accent: 305, values: { colorMode: 1, intensity: 1.4, bloomStrength: 1.4 } },
    ],
  },
  "Core Mass":      { mood: "Chill" },
  "Shock Bloom":    {
    mood: "Hype",
    variants: [
      { name: "Reactive", values: { bloomStrength: 1.5, bassGravity: 1.8 } },
      { name: "Chill",    values: { bloomStrength: 0.9 } },
    ],
  },
  Storm: {
    mood: "Hype",
    variants: [
      { name: "Wild", values: { intensity: 1.8, midCurl: 2.0 } },
      { name: "Calm", values: { intensity: 1.0, midCurl: 0.9 } },
    ],
  },
  "Lava Flow": {
    mood: "Hype",
    variants: [
      { name: "Glowing", values: { bloomStrength: 1.6, intensity: 1.6 } },
      { name: "Smoulder", values: { saturation: 0.7, intensity: 1.0 } },
    ],
  },

  // ------------- Curated additions -------------
  "DNA Helix":          { mood: "Tech" },
  "Pulse Vortex":       { mood: "Hype" },
  "Audio Tide":         { mood: "Chill" },
  "Orbital Rings":      { mood: "Cosmic" },
  "Galaxy Flythrough":  { mood: "Cosmic" },
  "Aurora Forest":      { mood: "Dreamy" },
  "Boid Flock":         { mood: "Chill" },
  "Wind Tunnel":        { mood: "Hype" },
  "Mercury Pool":       { mood: "Trippy" },
  "Mountain Range":     { mood: "Cinematic" },
  "Lava Sphere":        { mood: "Hype" },
  "Plasma Torus":       { mood: "Trippy" },
  "Drift Cube":         { mood: "Tech" },
  "Ribbon Drift":       { mood: "Dreamy" },
  "Lava Blob":          { mood: "Trippy" },
  "Waveform Glow":      { mood: "Tech" },
  "Pulse Halo":         { mood: "Hype" },
};

// Apply overlays in place — runs once at module load.
for (const scene of SCENES) {
  const overlay = SCENE_OVERLAYS[scene.name];
  if (!overlay) continue;
  if (overlay.mood && !scene.mood) scene.mood = overlay.mood;
  if (overlay.variants && !scene.variants) scene.variants = overlay.variants;
}

/**
 * Mood display order. Scenes without a mood fall under "All" at the end.
 */
export const SCENE_MOOD_ORDER: SceneMood[] = [
  "Showcase",
  "Living Forms",
  "Math Based",
  "Hype",
  "Chill",
  "Dreamy",
  "Trippy",
  "Cinematic",
  "Cosmic",
  "Tech",
];

/**
 * Group scenes by mood for grid rendering. The returned order matches
 * `SCENE_MOOD_ORDER`; an "All" bucket gathers any scene without a mood
 * tag so nothing silently disappears from the UI.
 */
export function scenesByMood(): Array<{ mood: SceneMood | "All"; scenes: ScenePreset[] }> {
  const buckets = new Map<SceneMood | "All", ScenePreset[]>();
  for (const m of SCENE_MOOD_ORDER) buckets.set(m, []);
  buckets.set("All", []);
  for (const s of SCENES) {
    const k: SceneMood | "All" = s.mood ?? "All";
    buckets.get(k)!.push(s);
  }
  const out: Array<{ mood: SceneMood | "All"; scenes: ScenePreset[] }> = [];
  for (const m of SCENE_MOOD_ORDER) {
    const arr = buckets.get(m)!;
    if (arr.length) out.push({ mood: m, scenes: arr });
  }
  const all = buckets.get("All")!;
  if (all.length) out.push({ mood: "All", scenes: all });
  return out;
}

const SCENE_LAYER_DEFAULTS: Partial<BioclastParams> = {
  layerParticles: false,
  layerStrings: false,
  layerLattice: false,
  layerAurora: false,
  layerWarp: false,
  layerCathedral: false,
  layerDnaHelix: false,
  layerVortex: false,
  layerTide: false,
  layerOrbital: false,
  layerGalaxy: false,
  layerAuroraForest: false,
  layerBoids: false,
  layerWind: false,
  layerMercury: false,
  layerMountains: false,
  layerEnergyOrb: false,
  layerWaveform: false,
};

/**
 * Merge layer reset defaults, `scene.values`, then `variant.values` into a
 * single param object suitable for `Object.assign(renderer.params, …)`. Pass
 * `null` for `variant` to apply just the base scene.
 */
export function resolveSceneValues(
  scene: ScenePreset,
  variant: SceneVariant | null,
): Partial<BioclastParams> {
  if (!variant) return { ...SCENE_LAYER_DEFAULTS, ...scene.values };
  return { ...SCENE_LAYER_DEFAULTS, ...scene.values, ...variant.values };
}
